import { memo, useId } from 'react';
import type {
  Piece as PieceType,
  KingIcon,
  CrownType,
  PieceShape,
  PieceStyle,
  Material,
} from '@draughts/shared';
import { Color, PieceType as Type, isFlatStyle } from '@draughts/shared';
import { usePreferencesStore } from '../../store/preferences-store';
import {
  PIECE_MATERIALS,
  PIECE_STYLE_GEOMETRY,
  STAR_POINTS,
  CROWN_ZIGZAG,
  catmullRomSegments,
  crownFitScale,
  CROWN_MIRROR_RISE,
  crownMirrorLayoutRise,
  crownMirrorRiseFor,
  crownRiseFor,
  CROWN_CIRCLET,
  crownCapFrom,
  crownShadeFor,
  crownSheenFor,
  DIAMOND_POINTS,
  getShapeGeom,
  iconFit,
  lowerEdgeMinK,
  shapeBottomK,
  reliefShades,
  resolveEmblemColor,
  emblemStrokeFor,
  FACE_SQUASH,
  DONUT_MARK_BAND,
  DONUT_BORE_DEPTH_K,
  LIGHT_DIR,
  LIP_SHIFT_K,
} from './piece-themes';
import type { ColorPair, CubicSegment, ShapeGeom, UnitPoint } from './piece-themes';
import { useStandalonePieceColorPair } from './use-appearance';
import { GRAIN_PATTERN_ID } from './material-defs-ids';
import { LOD_SPEC, reliefLod, usePieceLod, type PieceLod } from './piece-lod';
import Crown3d, { crownMirrorPath } from './crown3d';
import './Piece.css';

const CROWN_POINTS = CROWN_ZIGZAG;

/**
 * IL RIFLESSO SUL VETRO, e perché NON è uno specchio verticale del disegno del pezzo.
 *
 * Lo specchio è la LASTRA, e il pezzo la tocca con tutta la sua ellisse di BASE — centro
 * `faceY + bodyH`, semiassi `(rx, ry)`, ellittica perché `FACE_SQUASH` scorcia la faccia.
 * I punti di quel piano sono FISSI nella riflessione: l'immagine del pezzo è il cilindro
 * con la base ferma dov'è e la faccia portata a `faceY + 2·bodyH`. Di quel cilindro
 * virtuale la camera, che sta sopra, vede la faccia più ALTA — cioè l'ellisse di base, già
 * occupata dal pezzo — più la parete vicina, che scende fino al bordo inferiore
 * dell'ellisse specchiata. Quel che resta scoperto è dunque una FALCE di spessore
 * verticale esattamente `bodyH`, e si disegna con lo stesso sweep del fianco, ancorato
 * all'ellisse di base invece che alla faccia:
 *
 *   riflesso = sweepBody(faceY + bodyH, bodyH)      // il fianco = sweepBody(faceY, bodyH)
 *
 * TRE CONSEGUENZE, che la costruzione precedente — specchio attorno alla retta `baseY`,
 * schiacciato di 0.55 — sbagliava tutte e tre:
 *
 *  - la falce è LARGA QUANTO IL PEZZO e ne abbraccia l'arco inferiore. Specchiando attorno
 *    a `baseY`, che è il punto più BASSO della sagoma e non il piano d'appoggio, il
 *    riflesso nasceva da un PUNTO e si allargava scendendo: una goccia rovesciata, che
 *    legge come una macchia incollata sotto il pezzo e non come un riflesso;
 *  - non si disegna nessuna FACCIA, e con lei NESSUN RIVERBERO. Nello specchio il pezzo si
 *    vede DA SOTTO, e la faccia superiore è occlusa dalla parete. Ridipingerla capovolgeva
 *    con sé il suo gradiente radiale — `fillId` è in objectBoundingBox, quindi segue la
 *    trasformazione — e portava IN BASSO l'highlight che il materiale dichiara in alto a
 *    sinistra: un secondo speculare in contraddizione con la luce di tutto il resto del
 *    pezzo. La regola vale anche per uno speculare REINVENTATO invece che specchiato: qui
 *    c'è stato un fiocco di luce sul lato della luce, che voleva distinguere il vetro dalla
 *    plastica e che invece si leggeva come un adesivo — bianco pieno a 0.7 su una falce che
 *    al massimo arriva a 0.46, e su una dama impilata (`reflectDepth ≈ 0.84·radius`) un
 *    tondo quasi perfetto. Nello specchio della faccia non si vede niente: né il riverbero
 *    del vetro, né un suo sostituto;
 *  - il riempimento resta `wallId`, che è ORIZZONTALE: uno specchio orizzontale non scambia
 *    destra e sinistra, quindi il riflesso resta chiaro a sinistra e scuro a destra come il
 *    fianco che specchia. È lì che la luce torna coerente.
 *
 * Su una casella da 90 il riflesso finisce a `baseY + bodyH·(1 + REFLECT_GRAZE_DEPTH·i)`:
 * `y + 45.4` per una pedina e `y + 69.1` per una dama impilata sotto la camera, `y + 49.0` e
 * `y + 76.7` all'angolo. Dentro la casella sotto, ma mai fino al suo centro (`y + 90`), dove
 * sta il pallino dell'aiuto di mossa. Vedi la nota sullo z-order più giù. (I numeri sono del
 * tondo: `baseY` è il fondo della SAGOMA, e sulla stella e sul triangolo sta più in alto.)
 */

/**
 * Spessore del riflesso sugli stili PIATTI, in raggi. Là `bodyH` è zero e la formula fisica
 * darebbe riflesso nullo, ma "piatto + vetro" è una combinazione ammessa dalle preferenze e
 * una pedina piatta uno spessore ce l'ha comunque.
 */
const FLAT_REFLECT_K = 0.16;

/**
 * Riflettanza di Fresnel. Il vetro specchia tanto più quanto lo sguardo è radente: al centro
 * della lastra, sotto la camera, il riflesso è debole; agli angoli è più marcato e un po' più
 * lungo. L'incidenza normalizzata la calcola la SCACCHIERA (`glassIncidence` in
 * board-materials.ts), perché è una proprietà della CASELLA e non del pezzo.
 */
/**
 * DOVE COMINCIA E QUANTO SCENDE il riflesso della CORONA, in frazioni di `reflectDepth`.
 *
 * La corona sta sopra il pezzo, quindi la sua immagine sta SOTTO l'immagine del pezzo, e
 * comincia dove quella FINISCE — attaccata, non addosso. `CROWN_REFLECT_START` è una distanza
 * dal BORDO DEL RIFLESSO DEL CORPO, che è una curva e non una retta: si misura da `B(x)`,
 * l'arco inferiore della sagoma, quindi a 1 le due immagini si toccano a OGNI x, e la stessa
 * costante governa insieme la cima della banda e la curva del ritaglio.
 *
 * A 0.62 la corona entrava di `0.38 · reflectDepth` DENTRO la coda del riflesso del corpo —
 * 5.5 unità su una casella da 90 — per non leggere come un secondo oggetto. Ma un'immagine
 * che si sovrappone a quella che la genera non legge come la sua continuazione: legge come
 * spostata di un offset, ed è esattamente così che si vedeva.
 *
 * `CROWN_REFLECT_DEPTH` non è solo profondità: è ciò che decide lo SCHIACCIAMENTO, perché la
 * corona viene compressa dentro la banda. A 1.1 il fattore stava fra 0.30 (quadrato) e 0.84
 * (cuore) e i lobi del giglio si appiattivano in triangoli; a 1.4 sta fra 0.38 e 1.2 — sopra
 * 1 l'immagine è un filo STIRATA, che all'angolo è giusto: là si allunga anche il riflesso
 * del corpo. Nessun invariante `k < 1`.
 *
 * Su una casella da 90 con `radius 36`, forma tonda e corona classica: `baseY` sta a
 * `y + 40.2`, il riflesso del corpo finisce fra `y + 54.6` e `y + 58.2`, e la corona va da
 * `y + 45.7` a `y + 74.7` (all'angolo `y + 49.3` → `y + 83.4`) — con il ritaglio che ne scava
 * la cima lungo l'arco, così che sotto il centro del pezzo cominci dove il corpo finisce. Il
 * tetto è il CENTRO della casella sotto, `y + 90`, dove sta il pallino dell'aiuto di mossa: la
 * combinazione più profonda di tutte, imperiale su tondo all'angolo, si ferma a `y + 83.4`.
 * Vedi la nota in testa al file.
 */
const CROWN_REFLECT_START = 1;
const CROWN_REFLECT_DEPTH = 1.4;

const REFLECT_OPACITY_NADIR = 0.26;
const REFLECT_OPACITY_GRAZE = 0.46;
const REFLECT_GRAZE_DEPTH = 0.25;

/**
 * L'ombra di contatto. In RAGGI come ogni altra misura del pezzo — `bodyH`, lo scivolamento
 * del labbro, il riverbero — perché `<Piece>` è montato da 13.6 a 72 unità: pastiglie del menu
 * grafica, avatar, pezzi catturati, palette dell'editor, scacchiera. Un offset ASSOLUTO pesa il
 * 2.8% del diametro sulla scacchiera e il 14.7% su un avatar, ed era l'unica misura del file a
 * non scalare.
 *
 * Il rapporto dx/dy è quello CRUDO di `LIGHT_DIR` (0.5/0.866): l'ombra vive sul piano della
 * SCACCHIERA, che qui è disegnato senza scorcio — le caselle di `GameBoard` sono quadrate —
 * quindi NON va applicato `FACE_SQUASH`, che scorcia la faccia del PEZZO e non il piano.
 *
 * `dy` non è una taratura estetica. Sotto ~0.08 raggi l'ombra non esce mai da sotto la propria
 * sagoma: sulla scacchiera valeva 2 unità con una sfocatura pari, cioè 0.9px a 360px, cioè
 * un'ombra che non c'è. E l'ombra serve: sotto una luce da alto-sinistra la diagonale in basso
 * a destra la deve fare LEI. Il riflesso non può sostituirla nemmeno per sbaglio — uno specchio
 * lo governa la CAMERA e non la luce, quindi scende dritto — ma ora ha una forma propria, la
 * falce aderente all'ellisse di base, che non si confonde più con una seconda ombra.
 */
const SHADOW_DX_K = LIGHT_DIR.x / LIGHT_DIR.y;
const SHADOW_DY_K = 0.11;
/**
 * Questi offset NON hanno una versione «da scacchiera girata», ed è una misura, non una
 * scelta di stile.
 *
 * Per un giro il file ha avuto una costante che li negava quando la scacchiera era girata.
 * Era giusta su Chromium e rovesciata su Firefox: dove un filtro SVG vive sotto una
 * rotazione d'antenato, i due motori leggono `dx`/`dy` in spazi OPPOSTI — l'uno nello
 * spazio locale, ruotando il risultato dopo; l'altro nello spazio già ruotato. L'ombra
 * cadeva sopra il pezzo sull'uno o sull'altro, e nessun valore del segno poteva
 * accontentare entrambi.
 *
 * La cura non sta qui, sta nella FORMA dell'albero: vedi la testata della radice del
 * pezzo, in fondo al file. Lì il filtro torna a vedere l'identità, e questi numeri valgono
 * tali e quali nei due versi.
 */
const SHADOW_BLUR_K = 0.07;
const SHADOW_DY_TIGHT_K = 0.042;
const SHADOW_BLUR_TIGHT_K = 0.022;

/**
 * Il bordino illuminato della giuntura, sul lato IN OMBRA del pezzo e ai due bordi
 * della silhouette. Non è una taratura estetica: è il pavimento sotto il quale, su una
 * pedina quasi nera, la giuntura resta senza segnali (vedi il gradiente `seamLightId`).
 * `SEAM_LIGHT_EDGE` è dove la parete torna a `wallMid`, cioè al 67% del cammino da
 * `wallLight` a `wallDark`: stessa proporzione, riportata su [SEAM_LIGHT_MIN, 1].
 */
const SEAM_LIGHT_MIN = 0.4;
const SEAM_LIGHT_EDGE = 0.6;

/**
 * Quanto resta del bordino illuminato della giuntura DENTRO il foro della ciambella.
 *
 * Là il bordino non è illuminato da niente: sopravvive solo perché è ciò che fa leggere la
 * linea come una GIUNTURA e non come un graffio sulla parete. A piena intensità sarebbe una
 * luce dove la luce non arriva, ed è il motivo per cui la giuntura interna risulta più scura
 * di quella sul fianco pur usando lo stesso `shades.seam`.
 */
const BORE_SEAM_LIGHT_OPACITY = 0.35;

function getCrownPath(x: number, y: number, radius: number): string {
  const [first, ...rest] = CROWN_POINTS;
  const move = `M ${x + radius * first![0]} ${y + radius * first![1]}`;
  const lines = rest.map(([rx, ry]) => `L ${x + radius * rx} ${y + radius * ry}`).join(' ');
  return `${move} ${lines} Z`;
}

/** I vertici del contorno unitario, portati in coordinate assolute. */
function scaledPoints(
  points: readonly UnitPoint[],
  x: number,
  y: number,
  rx: number,
  ry: number,
): [number, number][] {
  return points.map(([px, py]) => [x + px * rx, y + py * ry]);
}

function polygonPath(pts: readonly [number, number][]): string {
  return `M ${pts.map(([px, py]) => `${px} ${py}`).join(' L ')} Z`;
}

const fmt = ([x, y]: [number, number], dy = 0): string => `${x} ${y + dy}`;

/** Il contorno chiuso e liscio, come catena di cubiche. */
function smoothClosedPath(segs: readonly CubicSegment[], dy = 0): string {
  const head = `M ${fmt(segs[0]!.p0, dy)}`;
  const body = segs.map((s) => `C ${fmt(s.c1, dy)} ${fmt(s.c2, dy)} ${fmt(s.p1, dy)}`).join(' ');
  return `${head} ${body} Z`;
}

/**
 * Spazzamento verticale del contorno LISCIO: il contorno traslato di `h` più una
 * banda per segmento, delimitata sopra e sotto dalla stessa cubica.
 *
 * La banda NON può essere il quadrilatero delle corde, che è la costruzione usata
 * per i poligoni veri: su un tratto convesso la cubica si gonfia oltre la corda, e
 * fra il bordo della faccia e la cima della parete resterebbe una fessura di fondo
 * scoperto. Delimitarla con la cubica stessa la chiude per costruzione.
 *
 * L'unione via nonzero richiede lo stesso verso per tutti i sub-path: come per i
 * poligoni il segno dell'area dipende da `(p1.x - p0.x) · h`, quindi le bande che
 * vanno verso sinistra si percorrono a partire dall'altro estremo.
 */
function smoothSweepPath(segs: readonly CubicSegment[], h: number): string {
  const subpaths: string[] = [smoothClosedPath(segs, h)];
  for (const s of segs) {
    if (s.p0[0] === s.p1[0]) continue; // banda di area nulla
    const forward = s.p1[0] > s.p0[0];
    const [a, ca, cb, b] = forward
      ? [s.p0, s.c1, s.c2, s.p1]
      : [s.p1, s.c2, s.c1, s.p0];
    subpaths.push(
      `M ${fmt(a)} C ${fmt(ca)} ${fmt(cb)} ${fmt(b)} ` +
        `L ${fmt(b, h)} C ${fmt(cb, h)} ${fmt(ca, h)} ${fmt(a, h)} Z`,
    );
  }
  return subpaths.join(' ');
}

/**
 * La catena inferiore percorsa sulla curva liscia, dal vertice di x massima a
 * quello di x minima (il verso in cui i contorni sono dichiarati). Serve alla
 * giuntura e al bordo visibile della parete, che altrimenti resterebbero spezzate
 * rette sotto una faccia curva.
 */
function smoothLowerChain(
  segs: readonly CubicSegment[],
  pts: readonly [number, number][],
  dy = 0,
): { start: [number, number]; end: [number, number]; curves: string } {
  let iMin = 0;
  let iMax = 0;
  for (let i = 1; i < pts.length; i++) {
    if (pts[i]![0] < pts[iMin]![0]) iMin = i;
    if (pts[i]![0] > pts[iMax]![0]) iMax = i;
  }
  const parts: string[] = [];
  for (let i = iMax; i !== iMin; i = (i + 1) % pts.length) {
    const s = segs[i]!;
    parts.push(`C ${fmt(s.c1, dy)} ${fmt(s.c2, dy)} ${fmt(s.p1, dy)}`);
  }
  return { start: pts[iMax]!, end: pts[iMin]!, curves: parts.join(' ') };
}

/** La catena inferiore come path a sé: è la giuntura fra i due dischi della dama. */
function smoothLowerChainPath(
  segs: readonly CubicSegment[],
  pts: readonly [number, number][],
  dy = 0,
): string {
  const { start, curves } = smoothLowerChain(segs, pts, dy);
  return `M ${fmt(start, dy)} ${curves}`;
}

/**
 * Il bordo VISIBILE della parete liscia: fianco destro, fondo curvo, fianco sinistro.
 * Come per i poligoni va disegnato a parte, perché uno stroke sullo spazzamento
 * mostrerebbe anche le cuciture interne fra le bande.
 */
function smoothWallEdgePath(
  segs: readonly CubicSegment[],
  pts: readonly [number, number][],
  h: number,
): string {
  const { start, end, curves } = smoothLowerChain(segs, pts, h);
  return `M ${fmt(start)} L ${fmt(start, h)} ${curves} L ${fmt(end)}`;
}

/** Il cerchio del foro, percorso in senso opposto non serve: il ritaglio è in evenodd. */
function circlePath(x: number, y: number, rx: number, ry: number): string {
  return `M ${x - rx} ${y} A ${rx} ${ry} 0 1 0 ${x + rx} ${y} A ${rx} ${ry} 0 1 0 ${x - rx} ${y} Z`;
}

/**
 * Contorno chiuso della forma del pezzo, scalato. Serve a disegnare TUTTI i solchi
 * concentrici in un solo `<path>` (un sub-path per anello): il numero di nodi non
 * cresce col numero di anelli, cosa che conta perché `React.memo` sul pezzo è già
 * neutralizzato da `cloneBoard` e ogni mossa ridisegna l'intera scacchiera.
 */
function outlinePath(geom: ShapeGeom, x: number, y: number, rx: number, ry: number): string {
  switch (geom.kind) {
    case 'polygon': {
      const pts = scaledPoints(geom.points!, x, y, rx, ry);
      return geom.smooth ? smoothClosedPath(catmullRomSegments(pts)) : polygonPath(pts);
    }
    case 'square': {
      // Stesso raggio d'angolo del corpo (rx * 0.12), così i solchi seguono la forma.
      const c = rx * 0.12;
      return (
        `M ${x - rx + c} ${y - ry} H ${x + rx - c} A ${c} ${c} 0 0 1 ${x + rx} ${y - ry + c} ` +
        `V ${y + ry - c} A ${c} ${c} 0 0 1 ${x + rx - c} ${y + ry} H ${x - rx + c} ` +
        `A ${c} ${c} 0 0 1 ${x - rx} ${y + ry - c} V ${y - ry + c} A ${c} ${c} 0 0 1 ${x - rx + c} ${y - ry} Z`
      );
    }
    case 'rhombus':
      return `M ${x} ${y - ry} L ${x + rx} ${y} L ${x} ${y + ry} L ${x - rx} ${y} Z`;
    case 'circle':
    case 'ellipse':
    default:
      // Cerchio come coppia di archi: un solo `A` non può chiudere 360°.
      return `M ${x - rx} ${y} A ${rx} ${ry} 0 1 0 ${x + rx} ${y} A ${rx} ${ry} 0 1 0 ${x - rx} ${y} Z`;
  }
}

/**
 * Comandi del bordo INFERIORE della sagoma, a partire dal punto più a sinistra
 * (`x - rx`, `y`) e fino a quello più a destra. Senza il `M` iniziale, così serve
 * sia da giuntura a sé stante sia da fondo della silhouette estrusa.
 *
 * Con l'asse y verso il basso, sweep-flag 0 percorre l'arco BASSO.
 */
function lowerOutlineCommands(kind: ShapeGeom['kind'], x: number, y: number, rx: number, ry: number): string {
  switch (kind) {
    case 'square': {
      const c = rx * 0.12;
      return (
        `V ${y + ry - c} A ${c} ${c} 0 0 0 ${x - rx + c} ${y + ry} ` +
        `H ${x + rx - c} A ${c} ${c} 0 0 0 ${x + rx} ${y + ry - c} V ${y}`
      );
    }
    case 'rhombus':
      return `L ${x} ${y + ry} L ${x + rx} ${y}`;
    case 'circle':
    case 'ellipse':
    default:
      return `A ${rx} ${ry} 0 0 0 ${x + rx} ${y}`;
  }
}

/**
 * La catena INFERIORE del poligono, da sinistra a destra: dal vertice di x minima
 * a quello di x massima passando dal lato basso.
 *
 * I contorni unitari sono dichiarati in senso orario sullo schermo, quindi
 * percorrendoli in avanti a partire dal vertice più a destra si scende lungo il
 * fianco destro, si attraversa il fondo e si risale a sinistra: è esattamente la
 * catena cercata, da rovesciare per ottenere l'ordine sinistra→destra.
 */
function lowerChainPoints(pts: readonly [number, number][]): [number, number][] {
  let iMin = 0;
  let iMax = 0;
  for (let i = 1; i < pts.length; i++) {
    if (pts[i]![0] < pts[iMin]![0]) iMin = i;
    if (pts[i]![0] > pts[iMax]![0]) iMax = i;
  }
  const chain: [number, number][] = [];
  for (let i = iMax; ; i = (i + 1) % pts.length) {
    chain.push(pts[i]!);
    if (i === iMin) break;
  }
  return chain.reverse();
}

/**
 * Solo l'arco inferiore: è la giuntura fra i due dischi della dama impilata, ed è il
 * bordo con cui finisce il riflesso.
 *
 * Restituisce anche i due ESTREMI perché il verso non è uniforme e non si può
 * indovinare: `lowerChainPoints` chiude con `.reverse()`, quindi i poligoni corrono da
 * x minima a x massima come `lowerOutlineCommands`, mentre `smoothLowerChain` — cioè il
 * cuore, l'unica forma liscia — corre al contrario. Chi deve CHIUDERE la catena in una
 * regione (`belowChainPath`) ha bisogno di sapere dove comincia e dove finisce, e
 * ricavarlo con un secondo `switch` lo farebbe divergere al primo ritocco della sagoma.
 */
interface LowerChain {
  d: string;
  from: [number, number];
  to: [number, number];
}

function lowerOutlineChain(geom: ShapeGeom, x: number, y: number, rx: number, ry: number): LowerChain {
  if (geom.kind === 'polygon') {
    const pts = scaledPoints(geom.points!, x, y, rx, ry);
    if (geom.smooth) {
      const segs = catmullRomSegments(pts);
      const { start, end } = smoothLowerChain(segs, pts);
      return { d: smoothLowerChainPath(segs, pts), from: start, to: end };
    }
    const chain = lowerChainPoints(pts);
    return {
      d: `M ${chain.map(([px, py]) => `${px} ${py}`).join(' L ')}`,
      from: chain[0]!,
      to: chain[chain.length - 1]!,
    };
  }
  return {
    d: `M ${x - rx} ${y} ${lowerOutlineCommands(geom.kind, x, y, rx, ry)}`,
    from: [x - rx, y],
    to: [x + rx, y],
  };
}

function lowerOutlinePath(geom: ShapeGeom, x: number, y: number, rx: number, ry: number): string {
  return lowerOutlineChain(geom, x, y, rx, ry).d;
}

/**
 * Tutto ciò che sta SOTTO l'arco inferiore della sagoma portato a quota `y`, chiuso a
 * `bottom`. È il ritaglio con cui il riflesso della corona esce da sotto il riflesso del
 * corpo seguendone la curva invece di una retta.
 *
 * Si allarga oltre `rx` prolungando i due estremi in orizzontale: la corona è più stretta
 * del pezzo, ma le sue punte specchiate non devono trovarsi un muro ai fianchi.
 */
function belowChainPath(
  geom: ShapeGeom,
  x: number,
  y: number,
  rx: number,
  ry: number,
  bottom: number,
): string {
  const { d, from, to } = lowerOutlineChain(geom, x, y, rx, ry);
  const wide = rx * 1.6;
  const [left, right] = from[0] <= to[0] ? [from, to] : [to, from];
  const lead = from[0] <= to[0] ? `M ${x - wide} ${left[1]} L ` : `M ${x + wide} ${right[1]} L `;
  const tail =
    from[0] <= to[0]
      ? `L ${x + wide} ${right[1]} L ${x + wide} ${bottom} L ${x - wide} ${bottom} Z`
      : `L ${x - wide} ${left[1]} L ${x - wide} ${bottom} L ${x + wide} ${bottom} Z`;
  return `${lead}${d.slice(2)} ${tail}`;
}

/**
 * Silhouette dello spazzamento verticale di un poligono, come sub-path multipli in
 * un solo `<path>` a fill-rule nonzero: il contorno traslato di `h` più un
 * quadrilatero per ogni lato.
 *
 * Non si può usare la costruzione analitica delle forme storiche — che chiude con
 * una corda orizzontale fra i due punti più larghi — perché su una forma CONCAVA
 * quella corda passa sopra le rientranze e le riempie: le insenature della stella
 * e l'incavo del cuore si tapperebbero di colore parete.
 *
 * L'unione via nonzero richiede che tutti i sub-path girino nello stesso verso.
 * Per uno spazzamento verticale l'area con segno del parallelogramma vale
 * `(b.x - a.x) · h`: basta invertire i lati che vanno verso sinistra e saltare
 * quelli verticali, che hanno area nulla.
 */
function polygonSweepPath(pts: readonly [number, number][], h: number): string {
  const subpaths: string[] = [
    polygonPath(pts.map(([px, py]) => [px, py + h] as [number, number])),
  ];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    if (a[0] === b[0]) continue;
    const quad: [number, number][] = [a, b, [b[0], b[1] + h], [a[0], a[1] + h]];
    subpaths.push(polygonPath(b[0] > a[0] ? quad : [...quad].reverse()));
  }
  return subpaths.join(' ');
}

/**
 * Il bordo VISIBILE della parete poligonale: fianco sinistro, fondo, fianco destro.
 * Va disegnato a parte, come tratto senza riempimento, perché uno stroke sul path
 * dello spazzamento mostrerebbe anche tutte le cuciture interne fra i quadrilateri.
 */
function polygonWallEdgePath(pts: readonly [number, number][], h: number): string {
  const chain = lowerChainPoints(pts);
  const left = chain[0]!;
  const right = chain[chain.length - 1]!;
  const bottom = chain.map(([px, py]) => `L ${px} ${py + h}`).join(' ');
  return `M ${left[0]} ${left[1]} ${bottom} L ${right[0]} ${right[1]}`;
}

/**
 * Silhouette del CILINDRO visto leggermente dall'alto: fianchi verticali dai due
 * punti più larghi della faccia, chiusi in basso dal bordo inferiore della stessa
 * sagoma traslato di `h`.
 *
 * È questo — non un disco piatto con un'ombra sotto — a far leggere il pezzo come
 * un oggetto con spessore. Il tratto in alto è una retta, ma resta nascosto sotto
 * la faccia, che lo copre esattamente fino ai suoi punti più larghi.
 */
function extrudePath(geom: ShapeGeom, x: number, faceY: number, rx: number, ry: number, h: number): string {
  return (
    `M ${x - rx} ${faceY} V ${faceY + h} ` +
    `${lowerOutlineCommands(geom.kind, x, faceY + h, rx, ry)} V ${faceY} Z`
  );
}

interface PieceProps {
  piece: PieceType;
  x: number;
  y: number;
  size: number;
  isSelected?: boolean;
  isDragging?: boolean;
  /** In transit through a multi-capture (intermediate hop): render translucent. */
  ghost?: boolean;
  /** Jumped this sequence but not yet removed: render faded until the final landing. */
  captured?: boolean;
  /**
   * Richiamo "questo pezzo può muoversi", acceso per un attimo dopo un tocco a vuoto.
   * È il NONCE del richiamo, non un booleano: la sua parità alterna il nome
   * dell'animazione, ed è così che il lampo riparte se si ritocca lo stesso pezzo
   * immobile mentre è ancora acceso (vedi Piece.css).
   */
  movableHint?: number | null;
  /**
   * L'SVG CHE MI OSPITA E' RUOTATO DI 180 GRADI IN CSS. Non "la scacchiera e' vista dal
   * nero": sono due cose diverse, e confonderle e' gia' costato un difetto.
   *
   * Solo `GameBoard` gira davvero l'SVG (`.game-board.flipped`), e allora il pezzo deve
   * contro-ruotare il corpo e l'emblema e NEGARE gli offset della propria ombra, o arrivano
   * a schermo capovolti. Chi invece specchia le COORDINATE in JS — GameReplay, EditorBoard,
   * PuzzlePreviewBoard — ha gia' il pezzo diritto e non deve passare questo prop: la
   * contro-rotazione la' non compensa niente, e' una rotazione netta di 180 gradi che
   * porta il fianco scuro del cilindro sopra la faccia.
   */
  flipped?: boolean;
  /** Resolved per-variant colors (passed by the board for the 'auto' theme). */
  colorPair?: ColorPair;
  /**
   * Livello di dettaglio del rilievo. Normalmente arriva dal `PieceLodContext` che la
   * scacchiera alimenta misurandosi; questo prop lo forza (swatch, anteprime, test).
   */
  lod?: PieceLod;
  /**
   * Scavalcano le preferenze salvate per questo singolo pezzo.
   *
   * Servono alle ANTEPRIME dei menu grafici, che devono mostrare com'è una forma o
   * uno stile *diverso* da quello attivo: un pezzo che legge lo store mostrerebbe
   * sette volte la stessa cosa. Stesso motivo di `colorPair` e `lod`, che esistono
   * già per swatch e anteprime.
   */
  shape?: PieceShape;
  style?: PieceStyle;
  kingIcon?: KingIcon;
  /** Forza il tipo di corona: serve alle anteprime, che mostrano i cinque tipi affiancati. */
  crownType?: CrownType;
  material?: Material;
  /**
   * La scacchiera sotto è di VETRO: il pezzo va raddoppiato dal proprio riflesso.
   *
   * Lo passa la SCACCHIERA (`boardMaterial === 'glass'`) e non lo legge il pezzo, perché
   * `boardMaterial` descrive la scacchiera e i pezzi si montano anche dove una
   * scacchiera non c'è — icone catture, pastiglie del menu, test. Stesso motivo di
   * `colorPair` e `lod`.
   */
  reflection?: boolean;
  /**
   * Incidenza dello sguardo sulla casella di questo pezzo: 0 sotto la camera (centro della
   * lastra), 1 agli angoli. Modula opacità e lunghezza del riflesso — il vetro specchia
   * tanto più quanto lo sguardo è radente.
   *
   * La calcola la SCACCHIERA con `glassIncidence`, per la stessa ragione di `reflection`:
   * la geometria della lastra descrive la scacchiera, e i pezzi si montano anche dove una
   * scacchiera non c'è. Il default è il valore MEDIO, che è quel che serve alle anteprime
   * senza una lastra quadrata sotto.
   */
  reflectionIncidence?: number;
}

export default memo(function Piece({ piece, x, y, size, isSelected = false, isDragging = false, ghost = false, captured = false, movableHint = null, flipped = false, colorPair, lod, shape: shapeOverride, style: styleOverride, kingIcon: kingIconOverride, crownType: crownTypeOverride, material: materialOverride, reflection = false, reflectionIncidence = 0.5 }: PieceProps) {
  const radius = size / 2;
  const isWhite = piece.color === Color.WHITE;
  const isKing = piece.type === Type.KING;

  const storedShape = usePreferencesStore((s) => s.pieceShape);
  const storedStyle = usePreferencesStore((s) => s.pieceStyle);
  const storedKingIcon = usePreferencesStore((s) => s.kingIcon);
  const storedCrownType = usePreferencesStore((s) => s.crownType);
  const storedMaterial = usePreferencesStore((s) => s.pieceMaterial);
  const pieceShape = shapeOverride ?? storedShape;
  const pieceStyle = styleOverride ?? storedStyle;
  const kingIcon = kingIconOverride ?? storedKingIcon;
  // Due assi distinti: l'emblema della dama e la forma della corona. Averli tenuti insieme
  // significava che sceglierne uno azzerava l'altro appena si cambiava stile.
  const crownType = crownTypeOverride ?? storedCrownType;
  const pieceMaterial = materialOverride ?? storedMaterial;
  const boardLod = usePieceLod();

  // Ripiego per gli usi senza scacchiera sotto (swatch, icone catture, test): là
  // 'auto' non ha una variante su cui risolversi e ricade sul classico, mentre
  // 'custom' i suoi due colori ce li ha e va onorato.
  const standalonePair = useStandalonePieceColorPair();
  const colorTheme: ColorPair = colorPair ?? standalonePair;
  // Fallback obbligatori: dopo un rollback di @draughts/shared il valore salvato in
  // localStorage può non esistere più in questa build, e un accesso secco farebbe
  // crashare l'intera scacchiera.
  const material = PIECE_MATERIALS[pieceMaterial] ?? PIECE_MATERIALS.plastic;
  const styleGeom = PIECE_STYLE_GEOMETRY[pieceStyle] ?? PIECE_STYLE_GEOMETRY['3d-stacked-engraved'];
  const fullGeom = getShapeGeom(pieceShape, radius);
  const relief = styleGeom.relief;

  // Senza misura della scacchiera si usa `size`: negli usi autonomi (icone catture,
  // swatch, test) 1 unità SVG vale circa 1px, quindi è la stima corretta.
  const tier = lod ?? boardLod ?? reliefLod(size);
  const lodSpec = LOD_SPEC[tier];

  /**
   * Su uno stile piatto la dama non ha volume proprio, quindi senza emblema sarebbe
   * disegnata identica a una pedina. Le impostazioni non offrono la combinazione, ma
   * la preferenza arriva anche da localStorage e dal server: il ripiego è load-bearing.
   */
  const resolvedIcon: KingIcon =
    kingIcon === 'none' && isFlatStyle(pieceStyle) ? 'star' : kingIcon;

  const stacked = isKing && relief !== undefined && styleGeom.kingBody === 'stacked';
  const crown3d = isKing && relief !== undefined && styleGeom.kingBody === 'crown3d';

  // Gli stili 3D sono cilindri visti leggermente dall'alto: la faccia è schiacciata
  // in verticale e il corpo scende sotto di essa. `geom` è la FACCIA, `fullGeom` la
  // pianta del pezzo; la larghezza non cambia mai, così il pezzo riempie la casella
  // come prima.
  const bodyH = relief ? radius * (stacked ? relief.kingHeight : relief.manHeight) : 0;
  const geom: ShapeGeom = relief
    ? { ...fullGeom, ry: fullGeom.ry * FACE_SQUASH }
    : fullGeom;

  // Corona 3D: un cerchietto appoggiato sulla faccia. `crown3dR` è il raggio dell'ellisse
  // della fascia e `crown3dH` l'altezza della sua parete; sopra il bordo superiore la corona
  // sale ancora di `crownRiseFor(tipo) · crown3dR` fra punte, sferette e archi — quanto,
  // dipende dal TIPO di corona scelto, che è più basso sull'imperiale e più alto sui gigli.
  //
  // `crownFitScale` non c'era finché la corona era un prisma largo 0.40 raggi: il cerchietto
  // ne occupa 1.5, e su triangolo, stella e cuore la faccia rientra ben prima del raggio.
  // È un TETTO, non un fattore: si prende il minore dei due, come fa `markR` con `iconFit`,
  // o le forme che stanno già larghe si rimpicciolirebbero due volte.
  const crown3dR = crown3d ? radius * Math.min(styleGeom.crownScale, crownFitScale(pieceShape)) : 0;
  const crown3dH = crown3d ? radius * styleGeom.crownHeight : 0;
  const crownRise = crown3d ? crown3dH + crownRiseFor(crownType) * crown3dR : 0;

  /**
   * Silhouette centrata sulla casella. Senza corona sporge di `geom.ry` sopra la faccia
   * e di `bodyH + geom.ry` sotto, e `ry` si semplifica lasciando il classico `bodyH / 2`;
   * con la corona sopra vince `crownRise` e il centraggio va rifatto per intero, o la
   * dama esce dal bordo superiore della casella (`overflow: hidden` in GameScreen.css).
   */
  const overTop = Math.max(geom.ry, crownRise);
  const faceY = y - (bodyH + geom.ry - overTop) / 2;
  /**
   * Origine della corona: il centro dell'ellisse del bordo SUPERIORE della fascia.
   *
   * Il bordo INFERIORE è concentrico alla faccia — stessa `squash`, quindi è letteralmente
   * un'ellisse disegnata su quel piano — e sta perciò a `faceY`. Ne segue che la corona non
   * sta tutta sopra la faccia: il suo arco anteriore scende dentro l'ellisse, com'è giusto
   * per un anello appoggiato e non sospeso sul bordo di dietro.
   */
  const crownFaceY = faceY - crown3dH;

  /**
   * Il punto più BASSO della sagoma: la punta dell'arco inferiore. `bodyH` è 0 sugli stili
   * piatti, quindi la formula vale per tutti.
   *
   * NON è il piano d'appoggio, ed è la distinzione da cui dipende tutto il riflesso: il
   * pezzo poggia sulla lastra con l'INTERA ellisse di base, che è alta `2·ry` a schermo, e
   * `baseY` ne è solo il punto inferiore. Qui serve perché è da lì in giù che il riflesso
   * esce da sotto il pezzo, cioè dove la sua sfumatura deve cominciare.
   *
   * E NON è `geom.ry`, che è il fondo del RIQUADRO. Per un giro lo è stato, e su tondo,
   * quadrato, rombo, ciambella e cuore i due numeri coincidono davvero; ma la stella scende
   * a 0.809 — i vertici bassi stanno a 234° e 306°, in mezzo c'è l'incavo — e il triangolo a
   * 0.72. Su quelle due forme `baseY` cadeva sotto il pezzo (4.6 unità su una casella da 90),
   * e ci portava sotto sia la sfumatura del riflesso, che finiva per tagliarsi di netto a un
   * quarto di opacità invece di spegnersi, sia la banda della corona, che si staccava.
   */
  const baseY = faceY + bodyH + geom.ry * shapeBottomK(pieceShape);
  /**
   * Il PIANO DELLO SPECCHIO: la retta per il CENTRO dell'ellisse di base. La differenza
   * con `baseY` — un intero semiasse `ry` — è tutta la nota in cima al file.
   */
  const reflectTop = faceY + bodyH;
  const graze = Math.min(1, Math.max(0, reflectionIncidence));
  const reflectDepth =
    (relief ? bodyH : radius * FLAT_REFLECT_K) * (1 + REFLECT_GRAZE_DEPTH * graze);
  const reflectOpacity =
    REFLECT_OPACITY_NADIR + (REFLECT_OPACITY_GRAZE - REFLECT_OPACITY_NADIR) * graze;
  // Un pezzo in mano è sollevato dal vetro: il riflesso ricompare all'atterraggio, che
  // è esattamente il momento in cui lo si vuole vedere.
  const showReflection = reflection && lodSpec.reflection && !isDragging;

  const uid = useId().replace(/[:]/g, '');
  const fillId = `piece-fill-${isWhite ? 'w' : 'b'}-${uid}`;
  const shadowId = `piece-shadow-${uid}`;
  const innerLightId = `piece-inner-light-${isWhite ? 'w' : 'b'}-${uid}`;
  const innerDarkId = `piece-inner-dark-${isWhite ? 'w' : 'b'}-${uid}`;
  const wallId = `piece-wall-${isWhite ? 'w' : 'b'}-${uid}`;
  const seamLightId = `piece-seam-light-${isWhite ? 'w' : 'b'}-${uid}`;
  const seamClipId = `piece-seam-clip-${uid}`;
  const crownWallId = `piece-crown-wall-${uid}`;
  const bodyClipId = `piece-clip-${uid}`;
  const boreClipId = `piece-bore-clip-${uid}`;
  const reflectSeamClipId = `piece-reflect-seam-clip-${uid}`;
  const reflectFadeId = `piece-reflect-fade-${uid}`;
  const reflectMaskId = `piece-reflect-mask-${uid}`;
  const crownReflectId = `piece-crown-reflect-${uid}`;
  const crownReflectClipId = `piece-crown-reflect-clip-${uid}`;

  const stops = isWhite ? colorTheme.white : colorTheme.dark;
  const strokeColor = isWhite ? colorTheme.whiteStroke : colorTheme.darkStroke;
  /**
   * La tinta dell'emblema si verifica contro la faccia che lo porta, e cede solo se
   * non regge il contrasto.
   *
   * L'oro dichiarato dai temi funziona sulle pedine scure ma spariva su quelle
   * chiare: sull'avana dell'italiana stava a 1.05 di rapporto, cioè oro su oro. Il
   * tono di riferimento è lo stop centrale del gradiente, quello che occupa la parte
   * di faccia dove l'emblema sta appoggiato.
   */
  const declaredCrown = isWhite ? colorTheme.whiteCrown : colorTheme.darkCrown;
  const faceTone = stops[1]?.color ?? stops[0]?.color ?? '#888888';
  /**
   * La corona 3D non prende la tinta dell'emblema: la RICAVA dal colore del pezzo. I temi la
   * dichiaravano oro, e per i colori 'auto' e 'personalizzato' l'oro era scritto a mano in
   * `colorPairFromVariant` — cioè chi sceglieva una tavolozza blu o verde si ritrovava
   * comunque la corona dorata, l'unico pezzo del disegno che non ubbidiva alla sua scelta.
   */
  const crownColor = resolveEmblemColor(declaredCrown, faceTone);
  // Il bordo dichiarato dal tema è tarato sulla SUA tinta: se la tinta è cambiata va
  // rifatto, o resterebbe un contorno scuro attorno a un emblema diventato scuro.
  const crownStroke =
    crownColor === declaredCrown
      ? (isWhite ? colorTheme.whiteCrownStroke : colorTheme.darkCrownStroke)
      : emblemStrokeFor(crownColor);
  /**
   * Il tono d'OMBRA della corona: tutto ciò che la corona riempie di scuro passa da qui, e
   * mai dal contorno del tema — che sui pezzi scuri è quasi nero e faceva sparire la fascia.
   * Vedi `crownShadeFor`.
   */
  const crownShade = crownShadeFor(crownColor);

  const innerLightStops = isWhite ? material.innerLightWhite : material.innerLightDark;
  const innerDarkStops = isWhite ? material.innerDarkWhite : material.innerDarkDark;
  const shadowOpacity = isWhite ? material.shadowOpacityWhite : material.shadowOpacityDark;

  // `mh-a`/`mh-b` alternano il nome dell'animazione a ogni richiamo: vedi Piece.css.
  const hintClass = movableHint === null ? '' : ` piece-movable-hint ${movableHint % 2 ? 'mh-a' : 'mh-b'}`;
  const className = `piece${isSelected ? ' piece-selected' : ''}${isDragging ? ' dragging' : ''}${ghost ? ' piece-ghost' : ''}${captured ? ' piece-captured' : ''}${hintClass}`;

  function rhombusPoints(rx: number, ry: number): string {
    return `${x},${faceY - ry} ${x + rx},${faceY} ${x},${faceY + ry} ${x - rx},${faceY}`;
  }

  function polygonPoints(rx: number, ry: number): string {
    return geom
      .points!.map(([px, py]) => `${x + px * rx},${faceY + py * ry}`)
      .join(' ');
  }

  /**
   * Anello forato: contorno esterno più foro, ritagliato in evenodd. Il foro si
   * DILATA di quanto l'esterno si contrae, così la corona d'anello si assottiglia
   * da entrambi i lati invece di scivolare verso l'interno.
   */
  function holedPath(rx: number, ry: number, grow = geom.rx - rx): string {
    const holeRx = geom.rx * geom.hole! + grow;
    const holeRy = geom.ry * geom.hole! + grow;
    return `${circlePath(x, faceY, rx, ry)} ${circlePath(x, faceY, holeRx, holeRy)}`;
  }

  /** La faccia superiore del pezzo (sugli stili 3D è l'ellisse in cima al cilindro). */
  function bodyShape(extraProps: Record<string, unknown> = {}) {
    if (geom.hole !== undefined) {
      return <path d={holedPath(geom.rx, geom.ry)} fillRule="evenodd" {...extraProps} />;
    }
    switch (geom.kind) {
      case 'polygon':
        return geom.smooth ? (
          <path d={outlinePath(geom, x, faceY, geom.rx, geom.ry)} {...extraProps} />
        ) : (
          <polygon points={polygonPoints(geom.rx, geom.ry)} strokeLinejoin="round" {...extraProps} />
        );
      case 'square':
        return (
          <rect
            x={x - geom.rx}
            y={faceY - geom.ry}
            width={geom.rx * 2}
            height={geom.ry * 2}
            rx={geom.rx * 0.12}
            ry={geom.ry * 0.12}
            {...extraProps}
          />
        );
      case 'rhombus':
        return <polygon points={rhombusPoints(geom.rx, geom.ry)} {...extraProps} />;
      case 'ellipse':
        return <ellipse cx={x} cy={faceY} rx={geom.rx} ry={geom.ry} {...extraProps} />;
      case 'circle':
      default:
        // Solo il rilievo schiaccia `ry`: gli stili piatti restano un <circle>, così
        // il loro output non cambia di un carattere.
        return relief ? (
          <ellipse cx={x} cy={faceY} rx={geom.rx} ry={geom.ry} {...extraProps} />
        ) : (
          <circle cx={x} cy={faceY} r={geom.rx} {...extraProps} />
        );
    }
  }

  function innerShape(rShrink: number, fill: string) {
    const ix = geom.rx - rShrink;
    const iy = geom.ry - rShrink;
    if (geom.hole !== undefined) {
      return <path d={holedPath(ix, iy)} fillRule="evenodd" fill={fill} pointerEvents="none" />;
    }
    switch (geom.kind) {
      case 'polygon':
        return geom.smooth ? (
          <path d={outlinePath(geom, x, faceY, ix, iy)} fill={fill} pointerEvents="none" />
        ) : (
          <polygon points={polygonPoints(ix, iy)} fill={fill} pointerEvents="none" />
        );
      case 'square':
        return (
          <rect
            x={x - ix}
            y={faceY - iy}
            width={ix * 2}
            height={iy * 2}
            rx={ix * 0.12}
            ry={iy * 0.12}
            fill={fill}
            pointerEvents="none"
          />
        );
      case 'rhombus':
        return <polygon points={rhombusPoints(ix, iy)} fill={fill} pointerEvents="none" />;
      case 'ellipse':
        return <ellipse cx={x} cy={faceY} rx={ix} ry={iy} fill={fill} pointerEvents="none" />;
      case 'circle':
      default:
        return relief ? (
          <ellipse cx={x} cy={faceY} rx={ix} ry={iy} fill={fill} pointerEvents="none" />
        ) : (
          <circle cx={x} cy={faceY} r={ix} fill={fill} pointerEvents="none" />
        );
    }
  }

  // ── Rilievo e incisione ───────────────────────────────────────────────────
  // Calcolate SEMPRE, non solo con il rilievo: 'flat-engraved' incide una faccia
  // che non ha parete, e senza queste tinte i suoi solchi non avrebbero colore.
  const shades = reliefShades(stops, material.cut);

  // Sotto i ~16px lo zigzag della corona (e un anello inciso) diventano sub-pixel:
  // meglio un disco pieno, che resta visibile. La soglia è il TIER e basta — una
  // stella o un diamante a 12px sono fango su qualunque stile, non solo sui 3D.
  const markCollapsed = lodSpec.rings === 0;
  /**
   * Se la faccia porta l'emblema. Calcolato QUI e non accanto al disegno dell'emblema
   * perché è ciò che decide chi occupa il CENTRO della faccia: con l'emblema la dama
   * perde il bottone e i solchi che gli passerebbero sotto, senza lo riprende tutto.
   *
   * Sulla corona 3D non c'è mai: lì la marcatura È la corona, e la sua forma la sceglie
   * `crownType`. Una pietra da quattro pixel in mezzo alla fascia — che è quel che c'era
   * prima — non si vedeva a nessuna dimensione reale.
   */
  const showMark = isKing && !crown3d && resolvedIcon !== 'none';

  /**
   * Bersaglio tornito: i solchi concentrici più il bottone al centro.
   *
   * Le forme a punta non reggono la catena intera (`geom.maxRings`), e sulla dama
   * marcata i solchi si fermano dove comincia il medaglione (`kingRings`).
   */
  const source = showMark ? styleGeom.kingRings : styleGeom.rings;
  const ringKs = source.slice(0, Math.min(lodSpec.rings, geom.maxRings ?? Infinity));
  /**
   * Il bottone chiude il bersaglio, e c'è solo dove il centro è libero e la forma è
   * TORNIBILE: `maxRings === undefined` isola da sé tondo, quadrato e rombo — su un
   * cuore un bersaglio tornito non esiste — e il foro della ciambella se lo mangerebbe
   * (0.25 contro un buco di raggio 0.38).
   */
  const bossK =
    styleGeom.boss > 0 &&
    lodSpec.boss === 'step' &&
    !showMark &&
    geom.maxRings === undefined &&
    geom.hole === undefined
      ? styleGeom.boss
      : 0;

  /**
   * Le forme fantasia hanno punte acute — la punta del cuore è una cuspide vera —
   * dove due contorni concentrici si avvicinano a piacere: lì il solco sborderebbe
   * fuori dal corpo. Ritagliarlo sulla sagoma lo risolve una volta per tutte, e
   * copre anche il foro della ciambella. Le tre forme storiche non lo usano: il
   * loro albero SVG resta quello di sempre.
   */
  const needsGrooveClip = geom.kind === 'polygon' || geom.hole !== undefined;

  /**
   * Il labbro illuminato del solco, come TRASLAZIONE e non come contorno rimpicciolito.
   *
   * Con la luce che arriva dall'alto, ogni solco tornito ha acceso il labbro INTERNO
   * sul lato alto e quello ESTERNO sul lato basso, mentre sui fianchi la luce è
   * radente e il labbro torna a coincidere col solco: è esattamente ciò che produce
   * lo stesso contorno spostato in basso-destra. Un contorno RIMPICCIOLITO, invece,
   * accende tutto il giro allo stesso modo — cioè da nessuna direzione — ed è il
   * motivo per cui l'incisione leggeva come righe disegnate e non come tornitura.
   *
   * Direzione ed entità dello spostamento stanno in piece-themes.ts, accanto a
   * `markLandPx`: è quella la formula che ne dipende.
   */
  const lipShift = radius * lodSpec.strokeK * geom.lipFactor * LIP_SHIFT_K;
  const lipX = x + lipShift * LIGHT_DIR.x;
  // La faccia degli stili 3D è schiacciata: lo spostamento va schiacciato con lei, o
  // in verticale il labbro scavalcherebbe il solco vicino.
  const lipY = faceY + lipShift * LIGHT_DIR.y * (geom.ry / geom.rx);

  // Lo scalino del bottone è un segno del bersaglio come gli altri: entra nello stesso
  // path scuro, così eredita ritaglio, tinta e schiacciamento senza costare un nodo in
  // più. Nel labbro NO: il bottone non ha un labbro, il suo piano è il riempimento.
  const grooveKs = bossK > 0 ? [...ringKs, bossK] : ringKs;

  const grooveContent =
    grooveKs.length > 0 ? (
      <>
        <path
          className="piece-groove"
          d={grooveKs.map((k) => outlinePath(geom, x, faceY, geom.rx * k, geom.ry * k)).join(' ')}
          fill="none"
          stroke={shades.grooveDark}
          strokeWidth={radius * lodSpec.strokeK}
          strokeLinejoin="round"
          pointerEvents="none"
        />
        {ringKs.length > 0 && (
          <path
            className="piece-groove-lip"
            d={ringKs
              .map((k) => outlinePath(geom, lipX, lipY, geom.rx * k, geom.ry * k))
              .join(' ')}
            fill="none"
            stroke={shades.grooveLight}
            strokeWidth={radius * lodSpec.strokeK * 0.6}
            strokeLinejoin="round"
            pointerEvents="none"
          />
        )}
      </>
    ) : null;

  const grooves =
    grooveContent && needsGrooveClip ? (
      <g clipPath={`url(#${bodyClipId})`}>{grooveContent}</g>
    ) : (
      grooveContent
    );

  /**
   * Il piano rialzato del bottone. È il segnale SECONDARIO: quel che fa leggere un
   * bottone tornito è lo scalino d'ombra alla sua base, non il piano più chiaro — su
   * una pedina chiara una velatura schiarente sparisce da sola.
   *
   * Va comunque DOPO le rampe di luce, come i solchi: sotto lo lavano via del tutto.
   */
  const boss =
    bossK > 0 ? (
      <path
        className="piece-boss"
        d={outlinePath(geom, x, faceY, geom.rx * bossK, geom.ry * bossK)}
        fill={shades.bossTop}
        pointerEvents="none"
      />
    ) : null;

  // Il fianco del cilindro. Disegnato PRIMA della faccia, che ne copre la metà alta:
  // resta visibile la fascia di spessore, che è ciò che dà volume al pezzo.
  const wallPoints =
    relief && geom.kind === 'polygon' ? scaledPoints(geom.points!, x, faceY, geom.rx, geom.ry) : null;
  // Sulle forme lisce la parete deve seguire la STESSA curva della faccia: con le
  // corde resterebbe scoperta una fessura di fondo lungo tutto il bordo convesso.
  const wallSegs = wallPoints && geom.smooth ? catmullRomSegments(wallPoints) : null;

  /**
   * La silhouette di un volume estruso: il contorno del pezzo ancorato a `topY`, spazzato
   * verso il basso di `h`.
   *
   * È parametrica nell'ancoraggio perché la usano DUE cose che non condividono l'origine —
   * il fianco, che parte dalla faccia, e il RIFLESSO, che parte dall'ellisse di base — e i
   * tre rami per forma (liscia, poligonale, tornita) devono restare uno solo: se
   * divergessero, il riflesso smetterebbe di essere la stessa sagoma che specchia.
   */
  function sweepBody(topY: number, h: number): string {
    if (geom.kind === 'polygon') {
      const pts = scaledPoints(geom.points!, x, topY, geom.rx, geom.ry);
      return geom.smooth ? smoothSweepPath(catmullRomSegments(pts), h) : polygonSweepPath(pts, h);
    }
    return extrudePath(geom, x, topY, geom.rx, geom.ry, h);
  }

  /**
   * Il fianco. Una sola espressione perché la usano in due: il riempimento, e il RITAGLIO
   * della giuntura, che non deve poterlo sforare. Se fossero due espressioni separate, la
   * prima modifica alla sagoma le farebbe divergere proprio sull'invariante che il
   * ritaglio esiste per garantire.
   */
  const bodyD = relief ? sweepBody(faceY, bodyH) : null;

  /**
   * La sagoma del riflesso. Sulla CIAMBELLA va sottratta l'apertura del foro sul piano
   * d'appoggio, esattamente come il fianco sottrae quella sulla faccia: senza, il riflesso
   * tapperebbe il buco proprio sullo stile che deve mostrarlo.
   */
  const reflectD = showReflection
    ? geom.hole !== undefined
      ? `${sweepBody(reflectTop, reflectDepth)} ${circlePath(x, reflectTop, geom.rx * geom.hole, geom.ry * geom.hole)}`
      : sweepBody(reflectTop, reflectDepth)
    : null;

  /**
   * Gli omologhi di `wallPoints` e `wallSegs` ancorati al PIANO DELLO SPECCHIO invece che alla
   * faccia: servono al bordo del riflesso, che va disegnato a parte esattamente come quello
   * del fianco e per la stessa ragione — vedi la nota sulla ragnatela sopra `wall`.
   */
  const reflectPoints =
    showReflection && geom.kind === 'polygon'
      ? scaledPoints(geom.points!, x, reflectTop, geom.rx, geom.ry)
      : null;
  const reflectSegs = reflectPoints && geom.smooth ? catmullRomSegments(reflectPoints) : null;

  const wall =
    bodyD ? (
      wallPoints ? (
        // Riempimento e bordo su due path distinti: uno stroke sullo spazzamento
        // disegnerebbe anche le cuciture fra le bande, cioè una ragnatela.
        <>
          <path className="piece-wall" d={bodyD} fill={`url(#${wallId})`} pointerEvents="none" />
          <path
            className="piece-wall-edge"
            d={
              wallSegs
                ? smoothWallEdgePath(wallSegs, wallPoints, bodyH)
                : polygonWallEdgePath(wallPoints, bodyH)
            }
            fill="none"
            stroke={shades.wallStroke}
            strokeWidth={1}
            strokeLinejoin="round"
            pointerEvents="none"
          />
        </>
      ) : geom.hole !== undefined ? (
        // Ciambella: al fianco va sottratta l'apertura SUPERIORE del foro, o il
        // riempimento coprirebbe la zona da cui si deve vedere la casella
        // attraverso il pezzo. Fondo e bordo si separano perché lo stroke, sul
        // path forato, disegnerebbe anche il contorno del foro — un anello che
        // sul pezzo vero non esiste.
        <>
          <path
            className="piece-wall"
            d={`${bodyD} ${circlePath(x, faceY, geom.rx * geom.hole, geom.ry * geom.hole)}`}
            fillRule="evenodd"
            fill={`url(#${wallId})`}
            pointerEvents="none"
          />
          <path
            className="piece-wall-edge"
            d={bodyD}
            fill="none"
            stroke={shades.wallStroke}
            strokeWidth={1}
            pointerEvents="none"
          />
        </>
      ) : (
        <path
          className="piece-wall"
          d={bodyD}
          fill={`url(#${wallId})`}
          stroke={shades.wallStroke}
          strokeWidth={1}
          pointerEvents="none"
        />
      )
    ) : null;

  /**
   * Il foro passante della ciambella sugli stili 3D.
   *
   * Guardando il pezzo dall'alto e da davanti, dentro l'apertura si vedono DUE cose,
   * e la linea che le separa è la mezzaluna concava verso il basso dell'apertura di
   * fondo:
   *
   *  - in ALTO la parete interna del foro, cioè il lato LONTANO della canna: è quello
   *    che il punto di vista scopre, e proietta verso l'alto sullo schermo;
   *  - in BASSO la casella, là dove l'apertura di sopra e quella di sotto si allineano.
   *
   * La zona di parete interna è quindi `apertura superiore MENO apertura inferiore`:
   * la si ottiene con le due ellissi in evenodd, ritagliate sull'apertura superiore
   * perché la falce inferiore dell'altra cadrebbe fuori dal foro, sul fianco.
   *
   * L'apertura inferiore NON sta sul piano d'appoggio ma un po' più in su, a
   * `bodyH · DONUT_BORE_DEPTH_K`: a profondità piena la lente fra le due ellissi era
   * alta 0.10 raggi, cioè meno di 2px reali a 360px, e la casella non si leggeva —
   * il foro sembrava spessore. Vedi la nota sulla costante in piece-themes.ts.
   *
   * Quando il pezzo è più alto di quanto il foro sia profondo in proiezione — la dama
   * a doppio disco — le due ellissi sono disgiunte, l'evenodd riempie tutta l'apertura
   * e non si vede più attraverso: che è esattamente ciò che succede a una ciambella
   * spessa guardata da quell'angolo. Il fattore è una FRAZIONE di `bodyH` proprio per
   * non toccare quel caso: 0.84 · 0.80 resta ben oltre i 0.50 raggi di apertura.
   *
   * Prima qui c'era la sola apertura di fondo riempita di scuro, che dà il risultato
   * ROVESCIATO — casella sopra, pezzo sotto, separati da una corda dritta — cioè una
   * forma che non corrisponde a nessun oggetto reale.
   */
  const bore =
    relief && geom.hole !== undefined ? (
      <g className="piece-bore" clipPath={`url(#${boreClipId})`} pointerEvents="none">
        <path
          d={`${circlePath(x, faceY, geom.rx * geom.hole, geom.ry * geom.hole)} ${circlePath(x, faceY + bodyH * DONUT_BORE_DEPTH_K, geom.rx * geom.hole, geom.ry * geom.hole)}`}
          fillRule="evenodd"
          fill={shades.wallDark}
        />
      </g>
    ) : null;

  /**
   * Le due larghezze della giuntura, condivise dalla giuntura VERA e da quella RIFLESSA:
   * due tratti sullo stesso percorso, l'ombra della fessura che corre tutt'attorno e sopra
   * il bordino illuminato del disco inferiore, più stretto, che lascia scoperti due margini
   * scuri. La sua intensità gira con la luce del fianco (vedi `seamLightId`).
   *
   * `strokeK` è calibrato perché `radius * strokeK` renda circa 1px REALE al minimo di
   * ciascun tier (vedi piece-lod): il bordino parte da lì, e l'ombra è 2 volte tanto per
   * lasciargli un margine sopra e sotto.
   *
   * Stanno qui e non dentro `seam` perché la giuntura riflessa deve essere la STESSA
   * giuntura: se le due larghezze divergessero, smetterebbe di essere ciò che specchia.
   */
  const seamLightWidth = Math.max(radius * lodSpec.strokeK * 0.75, 1);
  const seamDarkWidth = seamLightWidth * 2;

  /** I due tratti della giuntura sullo stesso percorso, per il pezzo e per il suo riflesso. */
  const seamStrokes = (d: string, prefix: 'piece-seam' | 'piece-reflection-seam') => (
    <>
      <path
        className={prefix}
        d={d}
        fill="none"
        stroke={shades.seam}
        strokeWidth={seamDarkWidth}
        pointerEvents="none"
      />
      <path
        className={`${prefix}-light`}
        d={d}
        fill="none"
        stroke={`url(#${seamLightId})`}
        strokeWidth={seamLightWidth}
        pointerEvents="none"
      />
    </>
  );

  // Giuntura a metà del fianco: divide lo spessore in due dischi uguali, che è come
  // si riconosce una dama vera. Sta sotto la faccia perché parte dai punti più larghi
  // e scende, quindi non viene mai coperta.
  const seam = (() => {
    if (!stacked || !lodSpec.seam) return null;
    const d = lowerOutlinePath(geom, x, faceY + bodyH / 2, geom.rx, geom.ry);
    /**
     * RITAGLIATI sulla silhouette del corpo, e senza calotta tonda. Il percorso finisce
     * sui punti PIÙ LARGHI del pezzo e il tratto è centrato su di esso, quindi da solo
     * sborda di mezza larghezza — fino a 2px sulla scacchiera desktop, di più
     * nell'anteprima grande — oltre un contorno che arriva a `rx + 0.5`. Le due cose
     * servono entrambe e fanno lavori diversi: il ritaglio toglie lo sbordo LATERALE,
     * mentre la calotta va tolta perché sporge verso l'ALTO, e quel pezzo di tratto sta
     * dentro la silhouette — il ritaglio non lo toccherebbe. Tagliata netta, la fessura
     * finisce a filo del bordo, come una scanalatura che scompare girando attorno al
     * cilindro.
     */
    return <g clipPath={`url(#${seamClipId})`}>{seamStrokes(d, 'piece-seam')}</g>;
  })();

  /**
   * LA STESSA GIUNTURA, vista DENTRO il foro.
   *
   * Senza, la dama-ciambella ha come superficie più visibile un disco grigio piatto — cioè
   * l'unica parte del pezzo che non dice che è una dama, proprio dove la giuntura sul pezzo
   * vero si vedrebbe eccome.
   *
   * Perché è l'arco SUPERIORE, al contrario di quella sul fianco. Dentro il foro si vede la
   * metà LONTANA della parete interna, non quella vicina: una circonferenza orizzontale a
   * profondità `d` si proietta nell'ellisse del foro traslata in giù di `d`, e di quella
   * serve solo l'arco di sopra. Non va costruito: due ellissi congruenti sfalsate di `d > 0`
   * si incontrano a `y = d/2` e da lì in giù l'arco inferiore esce SEMPRE dall'apertura,
   * quindi basta disegnare l'ellisse intera e lasciare che `boreClipId` la tagli. L'arco
   * finisce a filo del bordo del foro, come quello esterno finisce a filo della silhouette.
   *
   * A metà della profondità APPARENTE (`bodyH · DONUT_BORE_DEPTH_K`), non di `bodyH`: il
   * `bore` è disegnato con la canna accorciata e mescolare le due scale metterebbe la
   * giuntura a una profondità che contraddice la parete su cui sta. È anche l'unica delle due
   * che si vede — a profondità piena la gobba dell'arco varrebbe 0.04 raggi, sotto il pixel.
   *
   * Il ritaglio è la sola apertura superiore, e basta: sulla dama impilata le due aperture del
   * `bore` sono disgiunte, quindi non esiste falce di casella che l'arco possa attraversare.
   * Quella disgiunzione è un'invariante sotto test, non una fortuna.
   */
  const boreSeam =
    bore && stacked && lodSpec.seam ? (
      <g className="piece-bore-seam" clipPath={`url(#${boreClipId})`} pointerEvents="none">
        {(() => {
          // Non `seamStrokes`: quello monta il gradiente `seamLightId`, tarato sulla larghezza
          // del PEZZO — dove cade il lato in ombra, dove stanno i bordi della silhouette. In un
          // foro largo un terzo non vuol dire più niente, e qui servono due tratti piatti.
          const d = circlePath(
            x,
            faceY + (bodyH * DONUT_BORE_DEPTH_K) / 2,
            geom.rx * geom.hole!,
            geom.ry * geom.hole!,
          );
          return (
            <>
              <path
                className="piece-bore-seam-dark"
                d={d}
                fill="none"
                stroke={shades.seam}
                strokeWidth={seamDarkWidth}
              />
              <path
                className="piece-bore-seam-light"
                d={d}
                fill="none"
                stroke={shades.seamLight}
                strokeOpacity={BORE_SEAM_LIGHT_OPACITY}
                strokeWidth={seamLightWidth}
              />
            </>
          );
        })()}
      </g>
    ) : null;

  /**
   * LA GIUNTURA NELLO SPECCHIO, cioè il solo dettaglio che il riflesso si porta dietro.
   *
   * Dove va. Il piano dello specchio è `reflectTop`, il centro dell'ellisse di base; la
   * giuntura vera è la circonferenza a quota `bodyH / 2` SOPRA quel piano, quindi la sua
   * immagine sta `bodyH / 2` sotto. L'allungo di Fresnel stira l'intero solido specchiato da
   * `bodyH` a `reflectDepth`, e l'ancoraggio si stira con lui: `reflectTop + reflectDepth / 2`.
   * Arco INFERIORE come lo sweep del fianco — uno specchio orizzontale non cambia quale metà
   * del cilindro è vicina alla camera.
   *
   * Che non sia mai coperta dal pezzo non è una fortuna né una taratura. `sweepBody` è, su
   * tutte e tre le famiglie di sagoma, la somma del contorno con un segmento verticale: se
   * `B(dx)` è il bordo inferiore del pezzo, quello del riflesso è `B(dx) + reflectDepth`, la
   * falce scoperta è la banda `[B(dx), B(dx) + reflectDepth]` — spessa `reflectDepth` a OGNI
   * x — e questo arco, che vale `B(dx) + reflectDepth / 2`, ne è la mezzeria a ogni x. Su una
   * dama impilata in una casella da 90 sono 15 unità di franco sopra e altrettante sotto.
   */
  const reflectSeam =
    showReflection && stacked && lodSpec.seam && reflectD ? (
      // Il ritaglio serve per la stessa ragione della giuntura vera — il tratto è centrato
      // sul percorso e ai punti più larghi sborda di mezza larghezza — e la maschera del
      // riflesso non lo sostituisce: taglia a `rx * 1.05`, cioè 1.8 unità su un raggio da 36,
      // mentre il tratto ne sborda 2.03 a tier `full`. Restavano due nasi scuri fuori sagoma.
      // Sulla ciambella il `clip-rule` di default non buca il foro del percorso, e non deve:
      // l'arco sta molto più in basso dell'apertura e il foro non lo incontra mai.
      <g clipPath={`url(#${reflectSeamClipId})`}>
        {seamStrokes(
          lowerOutlinePath(geom, x, reflectTop + reflectDepth / 2, geom.rx, geom.ry),
          'piece-reflection-seam',
        )}
      </g>
    ) : null;

  /**
   * Il riverbero speculare del vetro. Lo disegna SOLO la faccia: nello specchio della lastra
   * il pezzo si vede da sotto, e là non c'è nessuna faccia da far brillare (vedi la nota in
   * cima al file).
   *
   * Ancorato a `faceY` come ogni altro elemento della faccia — nucleo, solchi, bottone,
   * emblema — e non al centro della casella: su uno stile 3D la faccia sta `bodyH / 2` più in
   * alto, cioè 15 unità su una dama impilata da 90. Ancorato a `y` il riverbero finiva sotto il
   * centro della faccia, nella metà OPPOSTA all'highlight radiale della stessa superficie:
   * due speculari sullo stesso vetro, da due luci diverse. Sugli stili piatti `faceY === y`,
   * quindi là non cambia nulla.
   */
  const glintCx = x - geom.rx * 0.3;
  const glintCy = faceY - geom.ry * 0.42;

  // Vetro: il bordo lascia trasparire la casella (è ciò che lo fa leggere come vetro),
  // ma il nucleo resta OPACO — senza, un pezzo scuro sparisce sulla casella scura e
  // uno chiaro sulla casella chiara, e il mandato mobile-first non lo consente.
  const glassCore =
    material.translucent ? (
      <>
        <path
          className="piece-glass-core"
          // Sulla ciambella il nucleo è a sua volta un anello: un disco pieno
          // tapperebbe il foro proprio sullo stile che deve mostrarlo.
          d={
            geom.hole !== undefined
              ? holedPath(geom.rx * 0.58, geom.ry * 0.58, 0)
              : outlinePath(geom, x, faceY, geom.rx * 0.58, geom.ry * 0.58)
          }
          fillRule={geom.hole !== undefined ? 'evenodd' : undefined}
          fill={`url(#${fillId})`}
          pointerEvents="none"
        />
        {/* Riflesso speculare: l'unico elemento che distingue il vetro a colpo d'occhio.
            RITAGLIATO SULLA FACCIA, perché è una luce SULLA superficie e non può uscirne.
            L'ellisse è piazzata in alto a sinistra su misure del BOUNDING BOX (`rx`, `ry`),
            che sulle sagome a punta non è la sagoma: sul triangolo cadeva quasi tutta fuori
            dal pezzo, come una macchia bianca sospesa sopra la punta. Il ritaglio sta su un
            gruppo e non sull'ellisse perché la `rotate(-30)` di quest'ultima porterebbe con
            sé anche il ritaglio. */}
        <g clipPath={`url(#${bodyClipId})`}>
          <ellipse
            className="piece-glass-glint"
            cx={glintCx}
            cy={glintCy}
            rx={geom.rx * 0.3}
            ry={geom.ry * 0.16}
            fill="rgba(255,255,255,0.75)"
            transform={`rotate(-30 ${glintCx} ${glintCy})`}
            pointerEvents="none"
          />
        </g>
      </>
    ) : null;

  /**
   * L'emblema si rimpicciolisce dove lo STILE è inciso — non dove a quel tier sono
   * rimasti dei solchi: senza incisione può riempire tutta la faccia, come ha sempre
   * fatto sugli stili piatti. Guardare `ringKs` renderebbe per giunta circolare la
   * scelta dei solchi, che ora dipende dall'emblema.
   *
   * La ciambella è esente anche da incisa: il suo unico solco sta a 0.82 e non è
   * l'emblema a doverlo evitare, semmai il contrario — un emblema rimpicciolito a
   * 0.8 finirebbe più stretto del foro (0.38 di raggio, cioè 0.76 di diametro) e
   * galleggerebbe dentro il buco invece di poggiare sull'anello.
   */
  const iconR =
    radius * (styleGeom.rings.length > 0 && geom.hole === undefined ? styleGeom.crownScale : 1);
  // Il contorno della corona non è simmetrico attorno alla propria origine (lo zigzag
  // sale a -0.25, la base scende a +0.35): appoggiato su una faccia schiacciata
  // scivolerebbe verso il bordo anteriore. Sugli stili 3D lo si rialza di quel disassamento.
  const iconY = relief ? faceY - iconR * 0.05 : y;

  /**
   * Corona 3D dello stile "3D con corona": un CERCHIETTO appoggiato sulla faccia, come se
   * il pezzo sotto fosse la testa del re. Il disegno sta tutto in `crown3d.tsx` — qui si
   * decidono solo dove va, quanto è grande e di che colore.
   *
   * Il TIPO di corona lo sceglie `crownType`, che è una preferenza a sé: su questo stile la
   * corona È la marcatura della dama, e un secondo medaglione sopra non ci sta (vedi
   * `showMark`). Tenere il tipo su `kingIcon` significava che sceglierlo cambiava l'emblema
   * degli altri stili.
   */
  const crownProps = crown3d
    ? {
        x,
        y: crownFaceY,
        R: crown3dR,
        h: crown3dH,
        type: crownType,
        color: crownColor,
        shade: crownShade,
        sheen: crownSheenFor(crownColor),
        stroke: crownStroke,
        strokeWidth: Math.max(radius * lodSpec.strokeK * 0.8, 1),
      }
    : null;

  const crown3dBody = crownProps ? (
    <Crown3d
      {...crownProps}
      detail={lodSpec.crown}
      fill={lodSpec.crown === 'solid' ? crownColor : `url(#${crownWallId})`}
      // La corona è scolpita nello stesso blocco del pezzo: prende i due overlay del
      // materiale, la sua venatura e, sul vetro, la sua trasparenza. Un filo meno del corpo
      // (0.72 contro 0.6): dietro il corpo c'è solo la casella, dietro la corona ci sono
      // anche la faccia del pezzo e le sue stesse punte posteriori.
      lightFill={`url(#${innerLightId})`}
      shadeFill={`url(#${innerDarkId})`}
      capFill={crownCapFrom(faceTone)}
      grainFill={
        material.grain && lodSpec.grain
          ? `url(#${GRAIN_PATTERN_ID[material.grain][isWhite ? 'light' : 'dark']})`
          : undefined
      }
      grainOpacity={material.grainOpacity}
      fillOpacity={material.translucent ? 0.72 : undefined}
    />
  ) : null;

  /**
   * La corona nel riflesso sul vetro.
   *
   * DOVE va, e perché non dove verrebbe da metterla. Specchiare attorno al piano d'appoggio
   * mette l'immagine della corona nella banda `[reflectTop, reflectTop + reflectDepth]` —
   * che è ESATTAMENTE la parte di riflesso coperta dal pezzo, perché di tutta la falce si
   * vede solo quel che sta sotto `baseY`, il punto più basso della sagoma. Provato: il
   * riflesso c'era nel DOM e non si vedeva un pixel.
   *
   * Quindi la corona si specchia dentro la FALCE VISIBILE: la sua cima cade sul fondo della
   * falce, la sua base appena sotto il bordo del pezzo. È una stilizzazione, come lo è già il
   * riflesso del corpo — che non è uno specchio ma lo stesso spazzamento ripetuto sotto il
   * piano — e dice quel che deve dire: su una lastra di vetro una dama riflette una corona,
   * non una pedina.
   *
   * Disegnata come SAGOMA SOLA (`crownMirrorPath`), non come corona in volume: nello specchio
   * non si vede l'interno di niente, e una lumeggiatura specchiata finirebbe dalla parte
   * sbagliata della luce. Costa una lastra, non otto nodi per dama — un nodo, per la
   * precisione, uno meno di prima.
   *
   * Ma la sagoma è quella del TIPO scelto, punte, perle, lobi, archi, globo e crocetta
   * comprese. `detail="solid"` sembrava la scelta giusta e non lo era: là il tipo si perde per
   * costruzione, e imperiale, gigliata e diadema davano tutte lo stesso zigzag di triangoli.
   * Ne veniva anche uno sfasamento — vedi `crownSpanMax`.
   */
  /**
   * DOVE COMINCIA, ora che le forme non sono più tutte tonde.
   *
   * Il riflesso del corpo non finisce su una retta: finisce, a ogni x, a
   * `B(x) + reflectDepth`, dove `B` è l'arco inferiore della sagoma (è la stessa identità
   * scritta nella nota della giuntura specchiata, qui sotto). Su un tondo `B` varia poco
   * sotto la corona e una banda orizzontale passava per buona; sulla stella, che sotto ha
   * una V, e sul cuore, che ha i lobi altissimi e una cuspide sola, la corona restava una
   * barra orizzontale staccata di 6 e di 15 unità su una casella da 90.
   *
   * Quindi la banda comincia dove `B` è più ALTO sotto la corona — lì la corona si vede
   * tutta — e dove la sagoma scende più a fondo è il RITAGLIO a scavarla seguendo la curva
   * (`crownReflectClipId`). Con `CROWN_REFLECT_START` a 1 la curva di taglio È il bordo del
   * riflesso del corpo: la stessa catena (`lowerOutlineChain`) alla stessa quota, quindi le
   * due immagini combaciano per costruzione invece che per taratura.
   */
  const crownHalfSpan = crown3d ? crown3dR / geom.rx : 0;
  const crownReflectTop =
    reflectTop +
    geom.ry * lowerEdgeMinK(pieceShape, crownHalfSpan) +
    reflectDepth * CROWN_REFLECT_START;
  /**
   * Il fondo resta ancorato a `baseY`, il punto più basso della sagoma: su tondo, cuore,
   * rombo, quadrato e ciambella è il numero di sempre, e risale solo dove risale il pezzo.
   */
  const crownReflectEnd = baseY + reflectDepth * (CROWN_REFLECT_START + CROWN_REFLECT_DEPTH);
  /**
   * Lo schiacciamento si calcola sull'ESTENSIONE INTERA della corona, non sulla sola
   * `crownRise`: la lastra scende sotto `faceY` per il semiasse della fascia, e prendendo
   * `faceY` come riferimento quel pezzo finirebbe più in alto dell'inizio previsto — cioè di
   * nuovo addosso al riflesso del corpo, che è il difetto da cui si parte.
   *
   * Si misura dai due estremi della banda e non da `CROWN_REFLECT_DEPTH · reflectDepth`:
   * l'altezza della banda ora dipende da quanto la sagoma è irregolare sotto la corona.
   *
   * E l'ingombro è quello della corona ROVESCIATA — `crownMirrorRiseFor`, non `crownRiseFor`.
   * Non è un cavillo: `crownMirrorPath` disegna con la PROFONDITÀ girata, perché il
   * `scale(1, -k)` qui sotto la rimetta dritta e ribalti le sole altezze (vedi `rim` in
   * crown3d.tsx). Girando la profondità cambia quale punta è la più alta — non più quella di
   * dietro, che parte in alto sull'ellisse, ma la centrale del fronte, che ora somma la
   * profondità invece di sottrarla — e su tre tipi su cinque il disegno viene più alto:
   * classica 1.815 R contro 1.195.
   *
   * E si misura sul tipo PIÙ ALTO, non su quello scelto: la compressione è una sola per tutti
   * e cinque. Comprimere ciascuno quanto basta a riempire la banda sembrava innocuo e non lo
   * era — la banda è una proprietà della SAGOMA e della profondità del riflesso, e non sa
   * niente di quanto sia alta la corona. Il diadema, la più bassa di tutte, veniva compresso a
   * 0.70 contro lo 0.41 della classica, e sul cuore arrivava a 1.06: il riflesso disegnato più
   * alto della corona vera. Ora una corona bassa riflette corto e una alta lungo, e a riempire
   * la banda per intero è la sola classica.
   *
   * L'ingombro conta la sola parte SOTTO il piano di contatto — `crown3dH + rise · R`, senza
   * il semiasse della base — perché è l'unica che si vede: vedi l'ancora, qui sotto.
   *
   * `crownLayoutSpan` e `crownDrawnSpan` differiscono su un tipo solo, l'imperiale, ed è il
   * rialzo dichiarato di `crownMirrorLayoutRise`: la sua altezza viene dalla crocetta, che sta
   * sull'asse e nello specchio non guadagna profondità, mentre le punte degli altri stanno sul
   * fronte e la guadagnano. Rifletteva un quarto più corta della classica pur vedendosi alta
   * uguale. Sugli altri quattro i due ingombri coincidono e `k` resta uno solo per tutti.
   */
  const crownDrawnSpan = crown3dH + crownMirrorRiseFor(crownType) * crown3dR;
  const crownLayoutSpan = crown3dH + crownMirrorLayoutRise(crownType) * crown3dR;
  const crownSpanMax = crown3dH + CROWN_MIRROR_RISE * crown3dR;
  /** Quanto dev'essere lunga l'immagine: la banda, in proporzione all'altezza del tipo. */
  const crownImageLength =
    (crownReflectEnd - crownReflectTop) * (crownLayoutSpan / Math.max(crownSpanMax, 1e-6));
  const crownReflectK = crown3d ? crownImageLength / Math.max(crownDrawnSpan, 1e-6) : 0;
  /**
   * Il CENTRO della base della corona, che nello specchio finisce sul bordo del riflesso della
   * pedina.
   *
   * Per un giro qui c'era l'apice POSTERIORE della base — il punto più basso del disegno — e
   * la conseguenza si vedeva: l'ellisse della base ricascava sotto il bordo del riflesso della
   * pedina lasciando due rientranze tonde ai fianchi, cioè la corona mostrava un bordo suo
   * dove non ne deve avere. La base poggia SULLA faccia: nello specchio è coperta
   * dall'immagine della pedina e non si vede affatto. Ancorandone il centro, la sua metà
   * posteriore finisce sopra la curva di taglio e il `clipPath` la porta via — la corona
   * comincia a filo del bordo della pedina a OGNI x.
   */
  const crownReflectAnchor = faceY;
  const crown3dReflection =
    crownProps && showReflection ? (
      /* Il RITAGLIO va su un gruppo suo, senza trasformazione suo malgrado: in SVG il
         `transform` di un elemento stabilisce il sistema in cui viene risolto anche il suo
         `clip-path`, quindi sullo stesso gruppo della corona il ritaglio verrebbe ribaltato
         e stirato insieme a lei, e taglierebbe dalla parte sbagliata. Il `rotate(180)` del
         pezzo capovolto sta invece su un ANTENATO, e va benissimo: ruota ritaglio e
         contenuto insieme, come fa già con la giuntura specchiata. */
      <g clipPath={`url(#${crownReflectClipId})`}>
        <g
          className="piece-reflection-crown"
          transform={`translate(0 ${crownReflectTop + crownReflectK * crownReflectAnchor}) scale(1 ${-crownReflectK})`}
          opacity={reflectOpacity}
        >
          {/* La corona INTERA, in una sagoma sola: `crownMirrorPath`. Non `<Crown3d detail="solid">`,
              che pure una sagoma sola la disegna — là il tipo si perde per costruzione (punte
              forzate a triangolo, niente perle, archi né cresta di dietro) e tutti e cinque i
              tipi davano lo stesso riflesso.

              Riempita con una sfumatura SUA, che muore in trasparenza. Fuori dalla maschera del
              riflesso del corpo — che taglia via tutto sotto `baseY + reflectDepth` invece di
              attenuarlo — servirebbe una seconda maschera, cioè un secondo buffer per pezzo. Un
              gradiente con l'alfa negli stop fa lo stesso lavoro e non apre niente; il ritaglio
              qui sopra è un `clipPath`, che di buffer non ne apre.

              Senza contorno: un tratto non sfuma insieme al riempimento e lascerebbe una riga
              netta proprio dove l'immagine deve morire. */}
          <path
            className="piece-crown3d-reflection"
            d={crownMirrorPath(x, crownFaceY, crown3dR, crown3dH, crownType)}
            fill={`url(#${crownReflectId})`}
          />
        </g>
      </g>
    ) : null;

  // ── Emblema della dama ────────────────────────────────────────────────────
  // `iconOnCrown`, `markCollapsed` e `showMark` stanno più in alto, accanto ai solchi:
  // sono loro a decidere chi occupa il centro della faccia.

  const markX = x;
  /**
   * L'emblema si rimpicciolisce fin dove serve per stare DENTRO la forma.
   *
   * Senza, una stella o un diamante scalati su `radius` escono dai fianchi del
   * triangolo, dalle insenature della stella e dalla punta del cuore: le forme a
   * punta rientrano ben prima del raggio, e sugli stili 3D la faccia è per giunta
   * schiacciata in verticale mentre l'emblema resta tondo. Sul tondo e sul quadrato
   * il fattore vale 1 e non cambia niente.
   *
   * Sulla corona 3D non si arriva mai qui: `showMark` è falso, la corona È l'emblema.
   */
  const fit = iconFit(pieceShape, resolvedIcon, relief ? FACE_SQUASH : 1);
  const markR = Math.min(iconR, radius * fit.scale);
  // Lo spostamento è in frazioni del raggio dell'emblema, quindi va applicato DOPO
  // averlo dimensionato: su un triangolo la parte larga sta in basso, e scendere di
  // un pelo vale più che rimpicciolire ancora.
  const markY = iconY + fit.offsetY * markR;
  // Pieno dorato col bordo scuro, appoggiato sulla faccia.
  const markFill = crownColor;
  const markStroke = crownStroke;
  const markStrokeWidth = 1;

  /**
   * Quando la ciambella si marca con una fascia sull'anello invece che con l'emblema
   * scelto. Solo nei due casi in cui l'emblema sparirebbe dentro il foro:
   *
   * - `ring`, i cui contorni a 0.4/0.26 ci cadrebbero dentro (il foro ha raggio 0.38);
   * - il tier di collasso, dove il disco pieno da 0.32 è più stretto del foro.
   *
   * Negli altri casi l'emblema si disegna per intero e scavalca il foro, come una
   * pietra incastonata in un anello. Prima la fascia valeva per OGNI icona, quindi
   * sulla ciambella la riga "Icona dama" non faceva niente.
   */
  const markOnDonutBand =
    geom.hole !== undefined && (resolvedIcon === 'ring' || markCollapsed);

  function markShape() {
    if (markOnDonutBand) {
      return (
        <path
          d={circlePath(
            x,
            faceY,
            geom.rx * DONUT_MARK_BAND,
            geom.ry * DONUT_MARK_BAND,
          )}
          fill="none"
          stroke={crownColor}
          strokeWidth={radius * lodSpec.strokeK * 2}
        />
      );
    }
    if (markCollapsed) {
      // Sotto i ~16px ogni emblema è sub-pixel: resta un disco pieno, che si vede.
      return (
        <circle cx={markX} cy={markY} r={radius * 0.32} fill={crownColor} stroke={crownStroke} strokeWidth={0.5} />
      );
    }
    switch (resolvedIcon) {
      case 'ring':
        return (
          <path
            d={[0.4, 0.26]
              .map((k) => outlinePath(geom, markX, markY, markR * k, markR * k))
              .join(' ')}
            fill="none"
            stroke={crownColor}
            strokeWidth={markStrokeWidth}
            strokeLinejoin="round"
          />
        );
      case 'star':
        return (
          <polygon
            points={STAR_POINTS.map(([px, py]) => `${markX + px * markR * 0.62},${markY + py * markR * 0.62}`).join(' ')}
            fill={markFill}
            stroke={markStroke}
            strokeWidth={markStrokeWidth}
            strokeLinejoin="round"
          />
        );
      case 'diamond':
        return (
          <>
            <polygon
              points={DIAMOND_POINTS.map(([px, py]) => `${markX + px * markR * 0.85},${markY + py * markR * 0.85}`).join(' ')}
              fill={markFill}
              stroke={markStroke}
              strokeWidth={markStrokeWidth}
              strokeLinejoin="round"
            />
            {/* Cintura e sfaccettature: senza, a 12px la sagoma è un pentagono qualunque. */}
            <path
              d={
                `M ${markX - 0.55 * markR * 0.85} ${markY - 0.18 * markR * 0.85} ` +
                `L ${markX + 0.55 * markR * 0.85} ${markY - 0.18 * markR * 0.85} ` +
                `M ${markX - 0.28 * markR * 0.85} ${markY - 0.55 * markR * 0.85} ` +
                `L ${markX} ${markY + 0.6 * markR * 0.85} ` +
                `L ${markX + 0.28 * markR * 0.85} ${markY - 0.55 * markR * 0.85}`
              }
              fill="none"
              stroke={markStroke}
              strokeWidth={Math.max(markStrokeWidth * 0.7, 0.5)}
              strokeLinejoin="round"
            />
          </>
        );
      case 'crown':
      default:
        return (
          <>
            <path
              d={getCrownPath(markX, markY, markR)}
              fill={markFill}
              stroke={markStroke}
              strokeWidth={markStrokeWidth}
            />
            <rect
              x={markX - markR * 0.45}
              y={markY + markR * 0.15}
              width={markR * 0.9}
              height={markR * 0.2}
              fill={markFill}
              stroke={markStroke}
              strokeWidth={markStrokeWidth}
            />
          </>
        );
    }
  }

  const kingMark = showMark ? (
    <g className="piece-king-mark" pointerEvents="none">
      {markShape()}
    </g>
  ) : null;

  const bodyContent = (
    <>
      {wall}
      {bore}
      {boreSeam}
      {bodyShape({
        fill: `url(#${fillId})`,
        stroke: strokeColor,
        strokeWidth: 2,
        className: 'piece-circle',
        ...(material.translucent ? { fillOpacity: 0.6 } : {}),
      })}
      {glassCore}
      {material.grain && lodSpec.grain && (
        <g className="piece-grain" opacity={material.grainOpacity} pointerEvents="none">
          {/* Pezzo chiaro = texture chiara (acero fine), scuro = scura (noce marcato):
              la stessa logica texture1/texture2 delle caselle. */}
          {innerShape(1, `url(#${GRAIN_PATTERN_ID[material.grain][isWhite ? 'light' : 'dark']})`)}
        </g>
      )}
      {innerShape(1, `url(#${innerLightId})`)}
      {innerShape(1, `url(#${innerDarkId})`)}
      {boss}
      {grooves}
      {seam}
      {crown3dBody}
      {geom.bevelRing && (
        <circle
          cx={x}
          cy={y}
          r={geom.rx - 2}
          fill="none"
          stroke={strokeColor}
          strokeWidth={1}
          opacity={0.55}
          pointerEvents="none"
        />
      )}
    </>
  );

  const contenuto = (
    <>
      {/*
        IL RIFLESSO, sotto il pezzo e sotto la sua ombra di contatto. La costruzione — e
        perché NON è uno specchio verticale del disegno del pezzo — sta nella nota in cima
        al file: è la stessa sagoma estrusa del fianco, ancorata all'ellisse di BASE, e
        quel che ne resta scoperto è la falce che abbraccia l'arco inferiore del pezzo.

        Sta nel layer dei PEZZI, che è reso dopo gli aiuti di mossa, quindi in teoria
        potrebbe velarne uno. In pratica no, per tre ragioni: sulle varianti a cattura
        diagonale la casella subito sotto un pezzo è di parità OPPOSTA — non giocabile,
        mai una destinazione; il caso peggiore (dama impilata all'angolo della lastra, cioè
        alla massima riflettanza) finisce a `y + 76.7` su una casella da 90, contro un
        pallino d'aiuto che comincia a `y + 76.5`; e quei due decimi di sovrapposizione
        cadono dove la sfumatura vale esattamente zero.

        È una SILHOUETTE, non `bodyContent` — con UNA eccezione, la giuntura. Solchi, bottone
        ed emblema non si leggono a un quarto di opacità sotto una sfumatura, e costerebbero
        una decina di nodi per pezzo per niente: quelli restano fuori. La giuntura no, perché
        non è una rifinitura ma il SEGNALE che distingue una dama da una pedina, e una
        silhouette liscia lo cancella proprio dove il pezzo dichiara di essere alto il doppio:
        il riflesso di una dama diventava un blocco unico. Costa due nodi e un ritaglio, e
        solo sulle dame a doppio disco.

        E sta FUORI dal filtro d'ombra: un riflesso non proietta ombra, e un feDropShadow per
        riflesso sarebbe un secondo buffer per pezzo.
      */}
      {showReflection && reflectD && (
        <g className="piece-reflection" pointerEvents="none" aria-hidden="true">
          {/* La maschera sta su un gruppo senza transform proprio, così il suo spazio
              utente è lo stesso in cui vivono `x` e `baseY`. L'opacità sta qui e non su
              un terzo gruppo: la maschera un buffer lo apre comunque. */}
          <g mask={`url(#${reflectMaskId})`} opacity={reflectOpacity}>
            {/* Classe PROPRIA e non `piece-wall`: il riflesso non deve entrare nei
                conteggi dei test di rilievo né nelle regole di hover. Il riempimento è
                però lo STESSO `wallId` del fianco — orizzontale, quindi immune allo
                specchio — ed è ciò che tiene la luce del riflesso allineata a quella del
                pezzo: chiaro a sinistra, scuro a destra, in entrambi. */}
            <path
              className="piece-reflection-wall"
              d={reflectD}
              fill={`url(#${wallId})`}
              {...(geom.hole !== undefined ? { fillRule: 'evenodd' as const } : {})}
              /* Sulle forme ANALITICHE — tondo, quadrato, rombo, ciambella — lo spazzamento è
                 una silhouette chiusa senza sotto-sagome, e strokearlo è innocuo. Sulle altre
                 il bordo sta sul path qui sotto: vedi la nota sulla ragnatela sopra `wall`. */
              {...(reflectPoints ? {} : { stroke: shades.wallStroke, strokeWidth: 1 })}
            />
            {/* IL BORDO DEL RIFLESSO, lo stesso che il fianco si disegna — e per un giro non lo
                era: stava sullo SPAZZAMENTO, che su triangolo, stella e cuore è il contorno
                traslato più una banda per lato, e ne ripassava tutte le cuciture interne. Sul
                cuore, campionato a venti punti, erano undici trattini verticali dentro la falce
                a 2.9px l'uno dall'altro su una casella da 360: una rigatura.

                Non è comunque una rifinitura da togliere: senza, la falce è una macchia di
                colore e legge come un alone: sono i due tratti verticali ai fianchi — il bordo
                del pezzo, specchiato, e l'unico pezzo di contorno che cade dove la sfumatura è
                ancora piena — a dire che quella macchia ha una FORMA, cioè che è un'immagine e
                non una luce. */}
            {reflectPoints && (
              <path
                className="piece-reflection-wall-edge"
                d={
                  reflectSegs
                    ? smoothWallEdgePath(reflectSegs, reflectPoints, reflectDepth)
                    : polygonWallEdgePath(reflectPoints, reflectDepth)
                }
                fill="none"
                stroke={shades.wallStroke}
                strokeWidth={1}
                strokeLinejoin="round"
              />
            )}
            {reflectSeam}
          </g>
          {/* La corona sta FUORI dal gruppo mascherato, e più in basso del corpo: nello
              specchio l'immagine di ciò che sta più in alto è quella più lontana dal piano.
              Dentro la maschera sarebbe tagliata via, perché quella finisce dove finisce il
              riflesso del corpo. */}
          {crown3dReflection}
        </g>
      )}

      <g filter={`url(#${shadowId})`}>
        {/* `.piece-relief` è il segnale "questo stile ha un volume" su cui si regge
            Piece.relief.test.tsx. Non porta trasformazioni: il verso della scacchiera lo
            decide la radice, un piano più su. */}
        <g className={relief ? 'piece-relief' : 'piece-upright'}>{bodyContent}</g>
      </g>

      {kingMark}
    </>
  );

  /**
   * LA RADICE PORTA IL VERSO, e nessun discendente lo rifà.
   *
   * `.game-board.flipped` gira di 180° l'intero `<svg>` della scacchiera. Questa
   * contro-rotazione la annulla, e ciò che sta sotto è disegnato una volta sola, nel verso
   * in cui lo si legge: la luce da alto-sinistra, l'ombra in basso a destra, il riflesso
   * sotto il pezzo. Prima erano tre contro-rotazioni separate — corpo, riflesso, emblema —
   * più un segno negato sugli offset dell'ombra, cioè quattro posti da tenere d'accordo.
   *
   * ⚠️ L'INVARIANTE, ed è ciò per cui questa forma esiste: un filtro DIREZIONALE — il
   * `feDropShadow` dell'ombra di contatto, l'ombra di sollevamento di `.piece.dragging` in
   * Piece.css — vale solo dove la rotazione TOTALE che lo separa dalla scacchiera è nulla.
   * Da questa radice IN GIÙ lo è, e su questa radice STESSA anche: i 180° della scacchiera
   * e i 180° di qui si compongono in una traslazione, e una traslazione non cambia una
   * direzione. Quel che non si può fare è infilare una rotazione FRA la radice e un filtro,
   * che è esattamente com'era prima.
   *
   * Dove la rotazione totale non è nulla i due motori si dividono — Chromium legge gli
   * offset nello spazio locale e ruota il risultato dopo, Firefox li legge nello spazio già
   * ruotato — e l'ombra cade sotto il pezzo sull'uno e sopra sull'altro. Nessuna taratura
   * dei VALORI rimette a posto le cose: conta il POSTO. Vedi la testata delle costanti
   * dell'ombra, e il banco a due motori che l'ha misurato.
   */
  return (
    <g className={className} transform={flipped ? `rotate(180 ${x} ${y})` : undefined}>
      <defs>
        <radialGradient id={fillId} cx={material.cx} cy={material.cy} r={material.r}>
          {stops.map((s) => (
            <stop key={s.offset} offset={s.offset} stopColor={s.color} />
          ))}
        </radialGradient>
        <linearGradient id={innerLightId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={innerLightStops[0]} />
          <stop offset="35%" stopColor={innerLightStops[1]} />
        </linearGradient>
        <linearGradient id={innerDarkId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="65%" stopColor={innerDarkStops[0]} />
          <stop offset="100%" stopColor={innerDarkStops[1]} />
        </linearGradient>
        {(relief || showReflection) && (
          // Orizzontale: la luce colpisce il fianco da sinistra e gira attorno al
          // cilindro. Senza questa banda il fianco è una fascia piatta e il pezzo
          // torna a sembrare un disco con un bordo, non un oggetto con spessore.
          //
          // Lo dichiara anche uno stile PIATTO, che una parete non ce l'ha: il riflesso lo
          // usa lo stesso, ed è l'unico gradiente del pezzo che attraversa lo specchio
          // senza cambiare (è orizzontale, e uno specchio orizzontale non scambia i lati).
          <linearGradient id={wallId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={shades.wallMid} />
            <stop offset="16%" stopColor={shades.wallLight} />
            <stop offset="62%" stopColor={shades.wallDark} />
            <stop offset="100%" stopColor={shades.wallMid} />
          </linearGradient>
        )}
        {seam && (
          /**
           * Il bordino illuminato della giuntura, acceso sulla STESSA banda della
           * parete: il bordino è il bordo superiore del disco inferiore, e la sua
           * normale gira attorno al cilindro esattamente come quella del fianco,
           * quindi è pieno dove la parete è più chiara (16%) e minimo dov'è più
           * scura (62%). Stessi ancoraggi di `wallId`, che i due bbox coincidono.
           *
           * Prima era acceso IN OPPOSIZIONE — massimo stacco, ma una giuntura
           * illuminata da destra su un pezzo illuminato da sinistra: contro la faccia
           * radiale (highlight in alto a sinistra), contro `wallLight` e contro
           * `LIGHT_DIR`.
           *
           * Il minimo NON è zero, ed è la parte che va lasciata stare: il tratto
           * scuro è `darken(edge, 0.66)`, che su un bordo quasi nero non ha dove
           * scurirsi. Spegnendo il bordino proprio dove la parete è più scura, quel
           * pezzo di giuntura resterebbe senza NESSUNO dei due segnali — 0.019 di
           * stacco sul bordo #1A1A1A, cioè metà giuntura invisibile sulle dame
           * scure. Con 0.4 sta sopra la soglia 0.18 di piece-themes.test.ts e resta
           * comunque 2.5 volte più spento del picco: la direzione della luce si legge
           * lo stesso.
           */
          <linearGradient id={seamLightId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={shades.seamLight} stopOpacity={SEAM_LIGHT_EDGE} />
            <stop offset="16%" stopColor={shades.seamLight} stopOpacity={1} />
            <stop offset="62%" stopColor={shades.seamLight} stopOpacity={SEAM_LIGHT_MIN} />
            <stop offset="100%" stopColor={shades.seamLight} stopOpacity={SEAM_LIGHT_EDGE} />
          </linearGradient>
        )}
        {crown3d && (
          /* Il metallo della corona prende la luce ESATTAMENTE come la faccia: radiale,
              centrato dove il materiale mette il suo centro di luce — in alto a sinistra su
              tutti e quattro, largo e morbido sul marmo, stretto e duro sulla plastica. Era
              un lineare ORIZZONTALE, cioè l'unico pezzo del disegno illuminato da un'altra
              parte, e per giunta uguale su tutti i materiali. */
          <radialGradient id={crownWallId} cx={material.cx} cy={material.cy} r={material.r}>
            <stop offset="0%" stopColor={crownSheenFor(crownColor)} />
            <stop offset="45%" stopColor={crownColor} />
            <stop offset="100%" stopColor={crownShade} />
          </radialGradient>
        )}
        {/* La regione regge la crescita senza allargarsi, e l'argomento non dipende dal
            contesto perché tutto scala insieme: `dx + 3σ` vale 0.137·size contro 0.30·size di
            margine laterale, `dy + 3σ` vale 0.16·size contro 0.40·bboxH — nel caso peggiore
            (pedina 3D, bboxH = 0.86·size) 0.344·size. Allargarla costerebbe un buffer più
            grande su fino a 40 pezzi.

            Il margine largo va dalla PARTE IN CUI CADE L'OMBRA, e l'ombra cade SEMPRE in
            basso a destra: dentro questo gruppo il corpo non è mai ruotato, qualunque sia il
            verso della scacchiera. La regione quindi non si specchia più — per un giro lo ha
            fatto, insieme al segno degli offset, e per la stessa ragione sbagliata. */}
        <filter id={shadowId} x="-30%" y="-20%" width="160%" height="160%">
          <feDropShadow
            dx={radius * SHADOW_DY_K * SHADOW_DX_K}
            dy={radius * SHADOW_DY_K}
            stdDeviation={radius * SHADOW_BLUR_K}
            floodColor="#000"
            floodOpacity={shadowOpacity}
          />
          <feDropShadow
            dx={radius * SHADOW_DY_TIGHT_K * SHADOW_DX_K}
            dy={radius * SHADOW_DY_TIGHT_K}
            stdDeviation={radius * SHADOW_BLUR_TIGHT_K}
            floodColor="#000"
            floodOpacity={shadowOpacity * 0.65}
          />
        </filter>
        {seam && bodyD && (
          // La giuntura non può uscire dal corpo: stesso identico percorso che il
          // fianco riempie, quindi l'invariante non dipende da una taratura. Il foro
          // della ciambella non va sottratto — la giuntura non ci arriva mai, il suo
          // punto più alto sta a `bodyH / 2` sotto la faccia e il foro finisce molto
          // più in su.
          <clipPath id={seamClipId}>
            <path d={bodyD} />
          </clipPath>
        )}
        {relief && geom.hole !== undefined && (
          <clipPath id={boreClipId}>
            <ellipse cx={x} cy={faceY} rx={geom.rx * geom.hole} ry={geom.ry * geom.hole} />
          </clipPath>
        )}
        {reflectSeam && reflectD && (
          // Stessa invariante del ritaglio della giuntura vera, un piano più giù: la
          // giuntura riflessa non può uscire dalla falce che la porta, e il percorso è
          // ESATTAMENTE quello che la falce riempie.
          <clipPath id={reflectSeamClipId}>
            <path d={reflectD} />
          </clipPath>
        )}
        {showReflection && (
          <>
            {/* La sfumatura corre dal punto in cui la SAGOMA tocca il vetro (`baseY`) al
                fondo del riflesso. Sopra `baseY` il gradiente è in `pad`, cioè pieno: là
                stanno le due "corna" della falce, che escono ai lati del pezzo perché
                l'arco inferiore risale — e sono anch'esse al contatto, quindi è giusto che
                siano le più forti. La maschera invece parte da `reflectTop - ry`, che è il
                punto più alto che la sagoma estrusa può raggiungere sulle forme a punta. */}
            <linearGradient
              id={reflectFadeId}
              gradientUnits="userSpaceOnUse"
              x1={x}
              y1={baseY}
              x2={x}
              y2={baseY + reflectDepth}
            >
              <stop offset="0%" stopColor="#fff" stopOpacity="1" />
              <stop offset="45%" stopColor="#fff" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            {crown3d && (
              /* La sfumatura del riflesso della CORONA — che NON sfuma, e va detto qui invece
                 di lasciarlo scoprire a chi legge.

                 `userSpaceOnUse` risolve le coordinate nello spazio dell'elemento che
                 riferisce il gradiente, e quell'elemento sta DENTRO il gruppo trasformato:
                 `y1` e `y2` sono quote di schermo, ma vengono lette come quote LOCALI del
                 path. Su una casella da 90, tondo e classica, il disegno occupa localmente
                 `y ∈ [42.4, 112.7]` mentre il gradiente dichiara `[145.7, 174.7]`: tutto il
                 path cade PRIMA del primo stop e, con lo `spreadMethod` di default, ne prende
                 il colore. Il riflesso della corona è quindi a tinta piatta, 0.5 costante.

                 È lo stato approvato e non lo si cambia di soppiatto: rimetterlo in funzione
                 sfumerebbe via perle, globo e crocetta, cioè proprio quel che distingue un
                 tipo dall'altro. Se un giorno lo si vuole davvero, la scelta va rifatta a
                 occhi aperti — e allora `y1`/`y2` vanno portate nello spazio locale, cioè
                 espresse a partire da `crownReflectAnchor` invece che dalla banda. */
              <linearGradient
                id={crownReflectId}
                gradientUnits="userSpaceOnUse"
                x1={x}
                y1={crownReflectTop}
                x2={x}
                y2={crownReflectEnd}
              >
                <stop offset="0%" stopColor={crownColor} stopOpacity="0.5" />
                <stop offset="55%" stopColor={crownColor} stopOpacity="0.22" />
                <stop offset="100%" stopColor={crownColor} stopOpacity="0" />
              </linearGradient>
            )}
            {crown3d && (
              /* IL RITAGLIO DELLA CORONA: tutto ciò che sta sotto l'arco inferiore della
                 sagoma risalito di `CROWN_REFLECT_START · reflectDepth`, cioè sotto il bordo
                 del riflesso del corpo meno un pelo, a OGNI x. È quel che rende il riflesso
                 della corona una curva invece che una barra: senza, la banda cominciava alla
                 stessa quota sotto la punta del cuore e sotto i suoi lobi, che stanno
                 quattordici unità più su.

                 Chiuso sotto `crownReflectEnd`, con un semiasse di franco: la corona finisce
                 lì, e un ritaglio che finisse esattamente lì le mangerebbe le punte. */
              <clipPath id={crownReflectClipId}>
                <path
                  d={belowChainPath(
                    geom,
                    x,
                    reflectTop + reflectDepth * CROWN_REFLECT_START,
                    geom.rx,
                    geom.ry,
                    crownReflectEnd + geom.ry,
                  )}
                />
              </clipPath>
            )}
            <mask
              id={reflectMaskId}
              maskUnits="userSpaceOnUse"
              x={x - geom.rx * 1.05}
              y={reflectTop - geom.ry}
              width={geom.rx * 2.1}
              height={reflectDepth + geom.ry * 2}
            >
              <rect
                x={x - geom.rx * 1.05}
                y={reflectTop - geom.ry}
                width={geom.rx * 2.1}
                height={reflectDepth + geom.ry * 2}
                fill={`url(#${reflectFadeId})`}
              />
            </mask>
          </>
        )}
        {((needsGrooveClip && grooveKs.length > 0) || material.translucent) && (
          // Due committenti, stessa sagoma: i solchi delle forme a punta, che sborderebbero
          // dalle cuspidi, e il riverbero del vetro, che è una luce sulla faccia.
          <clipPath id={bodyClipId}>
            {/* Dentro un clipPath vale `clip-rule`, non `fill-rule`: senza questo
                il foro della ciambella non verrebbe escluso dal ritaglio. */}
            {bodyShape({ clipRule: 'evenodd' })}
          </clipPath>
        )}
      </defs>

      {contenuto}
    </g>
  );
});
